import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FiEdit2, FiSave, FiX, FiPlus, FiTrash2, FiUpload, FiFileText, FiEye,
    FiUser, FiCreditCard, FiBook, FiAward, FiGlobe, FiActivity, FiMapPin, FiPhone, FiCalendar, FiMail, FiShield, FiLock, FiCheckCircle, FiAlertTriangle, FiStar, FiChevronLeft, FiChevronRight, FiPrinter, FiCode, FiUsers
} from 'react-icons/fi';
import { RiDownloadCloudFill } from "react-icons/ri";
import { FaIdCard, FaAddressCard } from 'react-icons/fa';
import toast from 'react-hot-toast';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, differenceInYears, differenceInMonths } from 'date-fns';
import Tesseract from 'tesseract.js';
// html-to-image and jsPDF imports removed
import api from '../utils/api';
import ImageCropper from '../components/ImageCropper';
import { useUser } from '../contexts/UserContext';




// --- Constants & Config ---
const countryCodes = [
    { code: '+91', country: 'IN', flag: 'https://flagcdn.com/w20/in.png' },
    { code: '+1', country: 'US', flag: 'https://flagcdn.com/w20/us.png' },
    { code: '+44', country: 'GB', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: '+971', country: 'AE', flag: 'https://flagcdn.com/w20/ae.png' },
    { code: '+65', country: 'SG', flag: 'https://flagcdn.com/w20/sg.png' },
];

// --- Helper Functions ---
const validatePhone = (phone) => {
    // Validates Indian numbers: +91 or just 10 digits starting with 6-9
    if (!phone) return false;
    const clean = phone.replace(/\D/g, '').slice(-10);
    return /^[6-9]\d{9}$/.test(clean);
};

const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
};

const validateIFSC = (ifsc) => {
    const re = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return re.test(ifsc);
};

const validateAadhaar = (num) => {
    const re = /^\d{12}$/;
    return re.test(num ? num.replace(/\s/g, '') : '');
};

const validatePAN = (num) => {
    // Indian PAN structure: 3 Letters, 4th Char (Type), 5th Letter, 4 Digits, 1 Letter
    // 4th char types: P(Individual), C(Company), H(HUF), F(Firm), A(AOP), T(Trust), B(BOI), L(Local), J(Artificial), G(Govt)
    const re = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]{1}$/;
    return re.test(num ? num.toUpperCase() : '');
};

const validateBankAccount = (num) => {
    const re = /^\d{9,18}$/;
    return re.test(num);
};

// --- Sub-Components ---
const SectionCard = ({ title, icon: Icon, children, isEditing, onEdit, onSave, onCancel }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
    >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50 rounded-t-xl">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ffebee] text-[#C30010] rounded-lg">
                    <Icon size={18} />
                </div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{title}</h3>
            </div>
            <div>
                {!isEditing && onEdit && (
                    <button
                        onClick={onEdit}
                        className="p-2 text-gray-400 hover:text-[#C30010] hover:bg-[#ffebee] rounded-full transition-all"
                        title="Edit Section"
                    >
                        <FiEdit2 size={16} />
                    </button>
                )}
            </div>
        </div>
        <div className="p-3 sm:p-6">
            {children}
        </div>
        {isEditing && (
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
                <button
                    onClick={onCancel}
                    className="px-3 sm:px-4 py-1 sm:py-1.5 text-[10px] sm:text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    className="px-3 sm:px-4 py-1 sm:py-1.5 text-[10px] sm:text-xs font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg shadow-md shadow-red-100 transition flex items-center gap-1.5"
                >
                    <FiSave className="hidden sm:block" size={14} /> Save Changes
                </button>
            </div>
        )}
    </motion.div>
);

const Field = ({ label, value, icon: Icon, fullWidth, type }) => {
    const isTel = type === 'tel' && !!value;
    return (
        <div className={`flex flex-col mb-2 ${fullWidth ? 'col-span-full' : ''}`}>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</label>
            <div className="flex items-center gap-2 font-medium text-sm">
                {Icon && <Icon size={14} className="text-gray-400" />}
                {isTel ? (
                    <a href={`tel:${value.replace(/\s+/g, '')}`} className="text-blue-600 hover:text-blue-700 transition font-bold flex items-center gap-1.5">
                        <span className="truncate">{value}</span>
                    </a>
                ) : (
                    <span className="truncate w-full text-gray-800">{value || '-'}</span>
                )}
            </div>
        </div>
    );
};


const customDatePickerStyles = `

    .react-datepicker {
        font-family: 'Inter', sans-serif;
        border: 1px solid #e5e7eb !important;
        border-radius: 16px !important;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        padding: 8px !important;
        background-color: white !important;
        font-size: 0.75rem !important;
        min-width: 240px !important;
    }
    .react-datepicker__header {
        background-color: white !important;
        border-bottom: 1px solid #f3f4f6 !important;
        padding-top: 0 !important;
        margin-bottom: 8px !important;
        padding-bottom: 8px !important;
    }
    .react-datepicker__day-names {
        display: flex !important;
        justify-content: space-between !important;
        margin-top: 8px !important;
    }
    .react-datepicker__day-name {
        color: #9ca3af !important;
        font-weight: 700 !important;
        width: 1.8rem !important;
        line-height: 1.8rem !important;
        margin: 0 !important;
        text-transform: uppercase;
        font-size: 0.75rem !important;
        text-align: center !important;
    }
    .react-datepicker__month {
        margin: 0 !important;
    }
    .react-datepicker__week {
        display: flex !important;
        justify-content: space-between !important;
    }
    .react-datepicker__day {
        margin: 0 !important;
        width: 1.8rem !important;
        line-height: 1.8rem !important;
        border-radius: 50% !important;
        color: #374151 !important;
        font-weight: 500 !important;
        font-size: 0.75rem !important;
        transition: all 0.2s ease;
    }
    .react-datepicker__day:hover {
        background-color: #fee2e2 !important;
        color: #C30010 !important;
    }
    .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
        background-color: #ec101fff !important;
        color: white !important;
        font-weight: 700 !important;
        box-shadow: 0 4px 6px -1px rgba(195, 0, 16, 0.3);
    }
    .react-datepicker__current-month, .react-datepicker-year-header {
        display: none !important;
    }
    .react-datepicker__day--today {
        border: 2px solid #C30010 !important;
        background-color: transparent;
        color: #C30010 !important;
        font-weight: 700 !important;
    }
    .react-datepicker__day--today.react-datepicker__day--selected {
        background-color: #C30010 !important;
        color: white !important;
    }
    .react-datepicker__day--disabled {
        color: #e5e7eb !important;
        cursor: default !important;
    }
    .react-datepicker__day--disabled:hover {
        background-color: transparent !important;
        color: #e5e7eb !important;
    }
    .react-datepicker__navigation {
        top: 24px !important;
    }
    .react-datepicker__navigation-icon::before {
        border-color: #4b5563 !important;
        border-width: 2px 2px 0 0 !important;
        width: 8px !important;
        height: 8px !important;
    }
    .react-datepicker-popper {
        z-index: 9999 !important;
    }
    /* Clean up the header selects */
    .react-datepicker__header select {
        padding: 2px 24px 2px 8px;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        font-size: 0.75rem;
        background-color: #f9fafb;
        cursor: pointer;
        outline: none;
        color: #374151;
        font-weight: 600;
        transition: all 0.2s;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 4px center;
        background-size: 12px;
    }
    .react-datepicker__header select:hover {
        border-color: #d1d5db;
        background-color: white;
    }
    .react-datepicker__header select:focus {
        border-color: #C30010;
        box-shadow: 0 0 0 2px rgba(195, 0, 16, 0.1);
    }
    .datepicker-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #f3f4f6;
    }
    .datepicker-done-btn {
        background-color: #C30010;
        color: white;
        padding: 6px 16px;
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: background-color 0.2s;
    }
    .datepicker-done-btn:hover {
        background-color: #a0000d;
    }
`;

const InputField = ({ label, name, value, onChange, type = "text", fullWidth, options, as = "input", error, required, list, min, max, step, placeholder, minDate, maxDate, disabled }) => (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'col-span-full' : ''}`}>
        <style>{customDatePickerStyles}</style>
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
            {label} {required && !disabled && <span className="text-red-500 font-bold">*</span>}
        </label>
        {as === 'select' ? (
            <select
                name={name}
                value={value || ''}
                onChange={onChange}
                disabled={disabled}
                className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800 cursor-pointer'} transition-all appearance-none`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1rem' }}
            >
                <option value="">Select...</option>
                {options && options.map((opt, i) => (
                    <option key={`${opt}-${i}`} value={opt}>{opt}</option>
                ))}
            </select>
        ) : as === 'textarea' ? (
            <textarea
                name={name}
                value={value || ''}
                onChange={onChange}
                rows={3}
                disabled={disabled}
                placeholder={placeholder}
                className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all resize-none`}
            />
        ) : type === 'date' ? (
            <div className="relative">
                <DatePicker
                    selected={value ? new Date(value) : null}
                    onChange={(date) => {
                        if (onChange) {
                            onChange({
                                target: {
                                    name,
                                    value: date ? format(date, 'yyyy-MM-dd') : ''
                                }
                            });
                        }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    disabled={disabled}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all block`}
                    wrapperClassName="w-full"
                    maxDate={maxDate}
                    minDate={minDate}
                    calendarClassName="custom-datepicker"
                    renderCustomHeader={({
                        date,
                        changeYear,
                        changeMonth,
                        decreaseMonth,
                        increaseMonth,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled,
                    }) => (
                        <div className="flex items-center justify-between px-2 py-2 bg-white border-b border-gray-100 gap-2">
                            <button
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                            >
                                <FiChevronLeft size={18} />
                            </button>

                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <select
                                    value={date.getMonth()}
                                    onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                                >
                                    {[
                                        "January", "February", "March", "April", "May", "June",
                                        "July", "August", "September", "October", "November", "December",
                                    ].map((option, index) => (
                                        <option key={option} value={index}>{option}</option>
                                    ))}
                                </select>
                                <select
                                    value={date.getFullYear()}
                                    onChange={({ target: { value } }) => changeYear(parseInt(value))}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                                >
                                    {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                            >
                                <FiChevronRight size={18} />
                            </button>
                        </div>
                    )}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <FiCalendar size={14} />
                </div>
            </div>
        ) : (
            <>
                <input
                    type={type}
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    list={list}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all`}
                />
                {list && options && (
                    <datalist id={list}>
                        {options.map(opt => <option key={opt} value={opt} />)}
                    </datalist>
                )}
            </>
        )}
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);


const DatePickerField = ({ label, selected, onChange, maxDate = new Date(), minDate, required, error }) => (
    <div className="flex flex-col gap-1.5 w-full">
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
            {label} {required && <span className="text-red-500 font-bold">*</span>}
        </label>
        <div className={`w-full relative ${error ? 'border border-red-500 rounded-xl' : ''}`}>
            <DatePicker
                selected={selected ? new Date(selected) : null}
                onChange={onChange}
                maxDate={maxDate}
                minDate={minDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                wrapperClassName="w-full"
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium text-gray-800 transition-all font-mono"
                onKeyDown={(e) => e.preventDefault()}
                renderCustomHeader={({
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                }) => (
                    <div className="flex items-center justify-between px-2 py-2 bg-white border-b border-gray-100 gap-2">
                        <button
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                        >
                            <FiChevronLeft size={18} />
                        </button>

                        <div className="flex items-center gap-2 flex-1 justify-center">
                            <select
                                value={date.getMonth()}
                                onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                            >
                                {[
                                    "January", "February", "March", "April", "May", "June",
                                    "July", "August", "September", "October", "November", "December",
                                ].map((option, index) => (
                                    <option key={option} value={index}>{option}</option>
                                ))}
                            </select>
                            <select
                                value={date.getFullYear()}
                                onChange={({ target: { value } }) => changeYear(parseInt(value))}
                                className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                            >
                                {Array.from({ length: 120 }, (_, i) => new Date().getFullYear() - 100 + i).map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                        >
                            <FiChevronRight size={18} />
                        </button>
                    </div>
                )}
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <FiCalendar size={14} />
            </div>
        </div>
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);

const PhoneInput = ({ label, name, value, onChange, error, required }) => {
    const [code, setCode] = useState('+91');
    const [number, setNumber] = useState('');

    useEffect(() => {
        if (value) {
            const parts = value.split(' ');
            if (parts.length > 1) {
                setCode(parts[0]);
                setNumber(parts[1]);
            } else if (value.startsWith('+')) {
                const found = countryCodes.find(c => value.startsWith(c.code));
                if (found) {
                    setCode(found.code);
                    setNumber(value.replace(found.code, '').trim());
                } else {
                    setNumber(value);
                }
            } else {
                setNumber(value);
            }
        }
    }, [value]);

    const handleNumberChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        if (val.length > 0 && !/^[6-9]/.test(val)) return;
        if (val.length <= 10) {
            setNumber(val);
            onChange({ target: { name, value: `${code} ${val}` } });
        }
    };

    const currentCountry = countryCodes.find(c => c.code === code) || countryCodes[0];

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
                {label} {required && <span className="text-red-500 font-bold">*</span>}
            </label>
            <div className="flex gap-3">
                <div className="relative w-28">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                        <img src={currentCountry.flag} alt="" className="w-5 h-3.5 rounded-sm object-cover border border-gray-100 shadow-sm" />
                    </div>
                    <select
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value);
                            onChange({ target: { name, value: `${e.target.value} ${number}` } });
                        }}
                        className="w-full pl-10 pr-2 py-2 bg-gray-50 border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-800 outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] transition-all cursor-pointer appearance-none"
                    >
                        {countryCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                </div>
                <input
                    type="text"
                    value={number}
                    onChange={handleNumberChange}
                    placeholder="XXXXXXXXXX"
                    className={`flex-1 px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold tracking-[0.1em] text-gray-800 transition-all font-mono`}
                />
            </div>
            {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
        </div>
    );
};

const FileUpload = ({ label, fileId, onUpload, currentFile, error, required }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
            {label} {required && <span className="text-red-500 font-bold">*</span>}
        </label>
        <div className="flex items-center gap-4">
            <label className={`cursor-pointer inline-flex items-center gap-2.5 px-3 py-2 bg-red-50 border ${error ? 'border-red-500 text-red-600 ring-1 ring-red-50' : 'border-red-100 text-[#C30010] hover:bg-red-100 hover:border-[#C30010]'} rounded-xl shadow-sm  text-[12.5px] transition-all group shrink-0`}>
                <FiUpload className={`${error ? 'text-red-500' : 'text-[#C30010] group-hover:text-[#a0000d]'} transition-colors`} size={16} />
                <span className={`font-bold ${error ? 'text-red-600' : 'text-[#C30010] group-hover:text-[#a0000d]'} transition-colors`}>Choose Image / PDF</span>
                <input type="file" className="hidden" onChange={(e) => onUpload(fileId, e.target.files[0])} accept="image/*,application/pdf" />
            </label>

            {currentFile ? (
                <div className="flex items-center gap-3 overflow-hidden">
                    <span className="inline-flex items-center gap-2 text-[#C30010] bg-[#ffebee] px-3 py-2 rounded-xl border border-red-100 truncate shadow-sm" title={typeof currentFile === 'string' ? currentFile.split('/').pop() : currentFile.name}>
                        <FiFileText size={16} className="shrink-0" />
                        <span className="text-[11px] font-bold truncate max-w-[150px] uppercase tracking-tighter">{typeof currentFile === 'string' ? currentFile.split('/').pop() : currentFile.name}</span>
                    </span>
                    <a href={typeof currentFile === 'string' ? currentFile : URL.createObjectURL(currentFile)} target="_blank" rel="noopener noreferrer" className="shrink-0 h-10 w-10 flex items-center justify-center bg-white border border-gray-200 text-[#C30010] hover:bg-[#ffebee] rounded-xl shadow-sm transition-all" title="View Document">
                        <FiEye size={18} />
                    </a>
                </div>
            ) : (
                <span className="text-[11px] text-gray-400 font-medium italic opacity-60">No file selected</span>
            )}
        </div>
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);

const SearchableSelect = ({ label, value, onChange, options, error, required, placeholder, hideIcon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const filteredOptions = options ? options.filter(opt =>
        opt.toLowerCase().includes((value || '').toLowerCase())
    ) : [];

    return (
        <div className={`flex flex-col gap-1.5 relative col-span-full ${isOpen ? 'z-50' : 'z-auto'}`} ref={wrapperRef}>
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
                {label} {required && <span className="text-red-500 font-bold">*</span>}
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder || "Type to search..."}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium text-gray-800 transition-all`}
                />
                {!hideIcon && (
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C30010] transition-colors"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                )}
            </div>

            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute top-full left-0 z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                    {filteredOptions.map((opt, i) => (
                        <div
                            key={i}
                            onClick={() => {
                                onChange(opt);
                                setIsOpen(false);
                            }}
                            className="px-4 py-2.5 text-[12.5px] text-gray-700 hover:bg-[#ffebee] hover:text-[#C30010] cursor-pointer border-b border-gray-50 last:border-0 transition-all font-medium"
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
            {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
        </div>
    );
};

// --- Main Component ---
export default function EmployeeDetails() {
    const navigate = useNavigate();
    const { user: globalUser, setUser: setGlobalUser } = useUser();

    // Derive roleId safely from globalUser
    const roleId = globalUser ? Number(globalUser.role_id || globalUser.user_role_id || 0) : 0;

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        profile_image: '',
        aadhaar_name: '', dob: null, blood_group: '', mobile: '', personal_email: '',
        address_permanent: '', address_communication: '',
        family_details: {}, // { father: {}, mother: {}, spouse: {} }
        joining_date: null,
        aadhaar_number: '', aadhaar_file: '', pan_number: '', pan_file: '',
        academic_details: [], certificates: [], bank_details: [],
        community: '', caste: '', religion: '',
        languages_speak: [],
        health_conditions: '', insurance_opt: 'OPT-OUT',
        nominee_name: '', nominee_relationship: '', nominee_dob: null, nominee_phone: '',
        identity_details: [], // Initialize new field
        skills: [], // Initialize Skills
        role_history: []
    });

    const [promotionModal, setPromotionModal] = useState({
        open: false,
        newRole: '',
        startDate: '',
        prevRoleEndDate: '',
        prevRoleStartDate: ''
    });

    const [verificationStatus, setVerificationStatus] = useState({ PAN: 'idle', AADHAAR: 'idle' });
    const [selectedIdType, setSelectedIdType] = useState(''); // New state for dropdown
    const [scannedData, setScannedData] = useState({ PAN: null, AADHAAR: null });
    const [identityModal, setIdentityModal] = useState({ open: false, type: null, file: null, preview: null });
    const [bankConfirmModal, setBankConfirmModal] = useState({ open: false, resolve: null });
    const scannerActiveRef = React.useRef(false);

    const [editingSection, setEditingSection] = useState(null);
    const [tempData, setTempData] = useState({});
    const [errors, setErrors] = useState({});
    const [showBirthdayPopup, setShowBirthdayPopup] = useState(false);
    const [config, setConfig] = useState({
        boards: ['CBSE', 'ICSE', 'State Board', 'IB'],
        degrees: [],
        degrees_map: {
            'UG': ['B.E', 'B.Tech', 'B.Sc', 'B.Com', 'B.A', 'BCA', 'BBA', 'B.Arch', 'B.Pharma', 'B.Des', 'LLB'],
            'PG': ['M.E', 'M.Tech', 'M.Sc', 'M.Com', 'M.A', 'MCA', 'MBA', 'M.Arch', 'M.Pharma', 'LLM'],
            'Diploma': ['Diploma in Engineering', 'Polytechnic', 'D.Pharma', 'D.Ed'],
            'PhD': ['PhD', 'Doctorate'],
            '10th': ['All Subjects', 'General'],
            '12th': ['Science', 'Commerce', 'Arts', 'Vocational']
        },
        colleges: [],
        banks: [],
        religions: [],
        communities: [],
        castes: [],
        skill_categories: []
    });

    const [isReadOnly, setIsReadOnly] = useState(true);

    // Update Read-Only status based on Global User Context & Target ID
    useEffect(() => {
        if (globalUser) {
            const roleId = Number(globalUser.role_id || globalUser.user_role_id || 0);
            const currentUserId = String(globalUser.user_id || globalUser.sub || globalUser.id);
            const targetId = getQueryParam('user_id');

            // 1. Admin (Role 1) can edit ANY profile
            if (roleId === 1) {
                setIsReadOnly(false);
            }
            // 2. Users can edit THEIR OWN profile
            else if (!targetId || String(targetId) === currentUserId) {
                setIsReadOnly(false);
            }
            // 3. Otherwise (viewing someone else's), it is Read-Only
            else {
                setIsReadOnly(true);
            }
        }
    }, [globalUser]);
    const [focusedScore, setFocusedScore] = useState(null); // Track focused score input index
    const [cropperImage, setCropperImage] = useState(null); // State for image to be cropped

    // Parse query params manually since we are using functional component patterns or native window
    const getQueryParam = (name) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }

    const todayDate = format(new Date(), 'dd/MM/yyyy');

    const handleVerifyBank = async (idx) => {
        const bank = tempData.bank_details[idx];
        if (!bank.account_number || !bank.ifsc) {
            toast.error("Enter Account Number & IFSC first");
            return;
        }

        const loadToast = toast.loading("Please wait, bank validation underway...");
        try {
            // Call the new backend endpoint
            const res = await api.post('/employee/verify-bank', {
                account_number: bank.account_number,
                ifsc: bank.ifsc,
                name: bank.holder_name
            });

            if (res.data.status) {
                if (res.data.verified) {
                    handleDeepChange('bank_details', idx, 'verified_name', res.data.registered_name);
                    handleDeepChange('bank_details', idx, 'verification_status', 'Verified');
                    // Auto-fetch/update name as requested
                    handleDeepChange('bank_details', idx, 'holder_name', res.data.registered_name);

                    toast.success(`Verified Successfully! Name: ${res.data.registered_name}`, { id: loadToast });
                } else {
                    handleDeepChange('bank_details', idx, 'verification_status', 'Failed');
                    toast.error("Bank Verification Failed (Penny Drop Rejected)", { id: loadToast });
                }
            } else {
                toast.error(res.data.message || "Verification Request Failed", { id: loadToast });
            }
        } catch (e) {
            console.error(e);
            toast.error("Verification Error: " + (e.response?.data?.messages?.error || e.message), { id: loadToast });
        }
    };

    // Persistence: Load unsaved data on mount
    useEffect(() => {
        const saved = localStorage.getItem('emp_unsaved_data');
        if (saved && !isReadOnly) { // Only load unsaved data if not in read-only mode
            try {
                const { editingSection: savedSection, tempData: savedTemp } = JSON.parse(saved);
                if (savedSection && savedTemp) {
                    setTempData(savedTemp);
                    setEditingSection(savedSection);
                    toast('Restored unsaved changes', { icon: '🔄' });
                }
            } catch (e) {
                console.error("Failed to parse saved data", e);
                localStorage.removeItem('emp_unsaved_data');
            }
        }
        fetchConfig();
        fetchDetails();
    }, []);

    const fetchConfig = async () => {
        try {
            const res = await api.get('/employee/config');
            if (res.data.status) setConfig(res.data.data);
        } catch (error) {
            console.error("Failed to fetch config", error);
        }
    };

    // Persistence: Save unsaved data on change
    useEffect(() => {
        if (isReadOnly) return; // Don't save to local storage in read-only mode
        if (editingSection && Object.keys(tempData).length > 0) {
            localStorage.setItem('emp_unsaved_data', JSON.stringify({ editingSection, tempData }));
        } else {
            localStorage.removeItem('emp_unsaved_data');
        }
    }, [editingSection, tempData, isReadOnly]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : `/employee/details`;

            // Use axios directly as imported, or api if preferred. Using axios to match existing patterns in file if any.
            // Actually the file uses axios import.
            const res = await api.get(url);

            if (res.data.status && res.data.data) {
                const fetchedData = res.data.data;

                // Birthday check logic
                if (fetchedData.dob) {
                    const dob = new Date(fetchedData.dob);
                    const today = new Date();
                    if (dob.getMonth() === today.getMonth() && dob.getDate() === today.getDate()) {
                        setShowBirthdayPopup(true);
                    }
                }

                setData(fetchedData);
                // Only overwrite tempData if we don't have unsaved local changes
                if (!localStorage.getItem('emp_unsaved_data')) {
                    setTempData(fetchedData);
                }



            } else {
                if (res.data.messages?.error === 'You are not authorized to view this profile') {
                    toast.error('You are not authorized to view this profile');
                    // perform navigation if needed, but navigate is not defined in this scope? 
                    // Check if useNavigate is hook used? It acts as a component, so yes.
                }
            }
        } catch (error) {
            console.error("Failed to fetch details", error);
            if (error.response && error.response.status === 403) {
                toast.error('You are not authorized to view this profile');
            } else {
                toast.error('Failed to load employee details');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        const toastId = toast.loading('Generating PDF...');

        try {
            const url = targetId
                ? `/employee/export-profile-pdf?user_id=${targetId}`
                : `/employee/export-profile-pdf`;

            const response = await api.get(url, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `Employee_Profile_${data.first_name || 'User'}.pdf`);
            document.body.appendChild(link);
            link.remove();

            toast.success('PDF Downloaded!', { id: toastId });
        } catch (error) {
            console.error("PDF Generation Error", error);
            toast.error('Failed to generate PDF.', { id: toastId });
        }
    };

    const startEdit = (section) => {
        if (isReadOnly) return;
        setTempData(JSON.parse(JSON.stringify(data)));
        setErrors({});
        setEditingSection(section);
    };

    const cancelEdit = () => {
        setEditingSection(null);
        setTempData({});
        setErrors({});
    };

    const validateSection = (section) => {
        const newErrors = {};
        const d = tempData;

        if (section === 'personal') {
            if (!d.profile_image) newErrors.profile_image = 'Profile Image Required';
            if (!d.aadhaar_name) newErrors.aadhaar_name = 'Name (as per Aadhaar) is Required';
            if (!d.dob) newErrors.dob = 'Date of Birth can\'t be empty';
            if (!d.blood_group) newErrors.blood_group = 'Blood Group is Required';
            if (!d.personal_email) newErrors.personal_email = 'Personal Email is Required';
            else if (!validateEmail(d.personal_email)) newErrors.personal_email = 'Invalid Email format';

            if (!d.address_permanent) newErrors.address_permanent = 'Permanent Address is Required';
            if (!d.address_communication) newErrors.address_communication = 'Communication Address is Required';

            // Mobile
            if (!d.mobile) newErrors.mobile = 'Mobile number is Required';
            else if (!validatePhone(d.mobile)) newErrors.mobile = 'Invalid Mobile Number';

            // Family - Specific fields inside family_details require validation
            if (!d.family_details?.father?.name) newErrors['father_name'] = 'Father Name Required';
            if (!d.family_details?.father?.contact) newErrors['father_contact'] = 'Father Contact Required';
            else if (!validatePhone(d.family_details.father.contact)) newErrors['father_contact'] = 'Invalid Father Contact';

            if (!d.family_details?.mother?.name) newErrors['mother_name'] = 'Mother Name Required';
            if (!d.family_details?.mother?.contact) newErrors['mother_contact'] = 'Mother Contact Required';
            else if (!validatePhone(d.family_details.mother.contact)) newErrors['mother_contact'] = 'Invalid Mother Contact';

            if (d.family_details?.spouse?.name && d.family_details?.spouse?.contact) {
                 if (!validatePhone(d.family_details.spouse.contact)) newErrors['spouse_contact'] = 'Invalid Spouse Contact';
            }
        }

        if (section === 'identity') {
            // Aadhaar Validation: Required if selected or already has data
            if (selectedIdType === 'AADHAAR' || d.aadhaar_number || d.aadhaar_file) {
                if (!d.aadhaar_number) newErrors['aadhaar_number'] = 'Aadhaar Number Required';
                else if (!validateAadhaar(d.aadhaar_number)) newErrors['aadhaar_number'] = 'Invalid Aadhaar Number (12 Digits)';
                if (!d.aadhaar_file) newErrors['aadhaar_file'] = 'Aadhaar Document Required';
            }

            // PAN Validation: Required if selected or already has data
            if (selectedIdType === 'PAN' || d.pan_number || d.pan_file) {
                if (!d.pan_number) newErrors['pan_number'] = 'PAN Number Required';
                else if (!validatePAN(d.pan_number)) newErrors['pan_number'] = 'Invalid PAN Number';
                if (!d.pan_file) newErrors['pan_file'] = 'PAN Document Required';
            }

            // Also check identity_details array if used
            (d.identity_details || []).forEach((item, idx) => {
                if (!item.type) newErrors[`id_${idx}_type`] = 'ID Type Required';
                if (!item.number) newErrors[`id_${idx}_number`] = 'ID Number Required';
                else {
                    if (item.type === 'Aadhaar' && !validateAadhaar(item.number)) newErrors[`id_${idx}_number`] = 'Invalid Aadhaar (12 digits)';
                    if (item.type === 'PAN' && !validatePAN(item.number)) newErrors[`id_${idx}_number`] = 'Invalid PAN';
                }
                if (!item.file) newErrors[`id_${idx}_file`] = 'Document Proof Required';
            });
        }

        if (section === 'experience') {
            const raw = d.role_history || [];
            // Map with original indices for accurate UI error reporting
            const indexed = raw.map((r, i) => ({ ...r, originalIdx: i }));

            // Sort by start_date to check chronological sequence
            const sorted = [...indexed].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

            sorted.forEach((role, sIdx) => {
                const idx = role.originalIdx;
                if (!role.job_role) newErrors[`role_${idx}_name`] = 'Job Role Required';
                if (!role.start_date) newErrors[`role_${idx}_start`] = 'Start Date Required';

                if (sIdx > 0) {
                    const prev = sorted[sIdx - 1];
                    // Overlap Check: Current start date cannot be before previous end date
                    if (prev.end_date && role.start_date && new Date(role.start_date) < new Date(prev.end_date)) {
                        newErrors[`role_${idx}_start`] = `Overlaps with previous role (ended ${format(new Date(prev.end_date), 'dd/MM/yyyy')})`;
                    }
                    // Strict Start Date Sequence
                    if (role.start_date && prev.start_date && new Date(role.start_date) <= new Date(prev.start_date)) {
                        newErrors[`role_${idx}_start`] = 'Role sequence start dates must be strictly increasing';
                    }
                }

                if (role.end_date && role.start_date && new Date(role.end_date) <= new Date(role.start_date)) {
                    newErrors[`role_${idx}_end`] = 'End date must be after start date';
                }
            });
        }

        if (section === 'academic') {
            (d.academic_details || []).forEach((item, idx) => {
                const score = parseFloat(item.percentage);
                if (!item.percentage) newErrors[`ac_score_${idx}`] = 'Score Required';
                else if (item.score_type === 'CGPA' && score > 10) {
                    newErrors[`ac_score_${idx}`] = 'CGPA cannot be > 10';
                } else if (item.score_type !== 'CGPA' && score > 100) {
                    newErrors[`ac_score_${idx}`] = 'Percentage cannot be > 100';
                }
                if (!item.year) newErrors[`ac_year_${idx}`] = 'Passing Year Required';
                if (!item.file) newErrors[`ac_file_${idx}`] = 'Document Required';
            });
        }

        if (section === 'certificates') {
            (tempData.certificates || []).forEach((cert, idx) => {
                if (!cert.type) newErrors[`cert_${idx}_type`] = 'Type Required';
                if (!cert.title) newErrors[`cert_${idx}_title`] = 'Title Required';
                if (!cert.issuing_authority) newErrors[`cert_${idx}_authority`] = 'Authority Required';
                if (!cert.issue_date) newErrors[`cert_${idx}_issue`] = 'Issue Date Required';
                else if (cert.issue_date > new Date().toISOString().split('T')[0]) newErrors[`cert_${idx}_issue`] = 'Future Date Invalid';

                if (cert.type !== 'Award') {
                    if (!cert.validity_type) newErrors[`cert_${idx}_validity`] = 'Validity Type Required';

                    if (cert.validity_type === 'Renewable') {
                        if (!cert.expiry_date) newErrors[`cert_${idx}_expiry`] = 'Expiry Date Required';
                        else if (cert.issue_date && cert.expiry_date <= cert.issue_date) newErrors[`cert_${idx}_expiry`] = 'Expiry must be after Issue Date';
                    }
                }

                if (!cert.file) newErrors[`cert_${idx}_file`] = 'Certificate Document Required';
            });
        }

        if (section === 'bank') {
            (d.bank_details || []).forEach((bank, idx) => {
                if (!bank.bank_name) newErrors[`bank_${idx}_name`] = 'Bank Name Required';
                if (!bank.ifsc || !validateIFSC(bank.ifsc)) newErrors[`bank_${idx}_ifsc`] = 'Invalid IFSC';
                if (!bank.branch) newErrors[`bank_${idx}_branch`] = 'Branch Required';
                if (!bank.account_number) newErrors[`bank_${idx}_acc`] = 'Account Number Required';
                else if (!validateBankAccount(bank.account_number)) newErrors[`bank_${idx}_acc`] = 'Invalid Account Number';

                // Expiry Validation (MM/YYYY) - field is optional
                if (bank.expiry_date) {
                    const [m, y] = (bank.expiry_date || '').split('/').map(Number);
                    if (!m || !y || m < 1 || m > 12) {
                        newErrors[`bank_${idx}_expiry`] = 'Invalid Format (MM/YYYY)';
                    } else {
                        const now = new Date();
                        const currM = now.getMonth() + 1;
                        const currY = now.getFullYear();
                        if (y < currY || (y === currY && m < currM)) {
                            newErrors[`bank_${idx}_expiry`] = 'Account Expired / Card Expired';
                        }
                    }
                }
            });
        }

        if (section === 'health') {
            if (d.insurance_opt === 'OPT-IN') {
                if (!d.nominee_name) newErrors.nominee_name = 'Nominee Name Required';
                if (!d.nominee_relationship) newErrors.nominee_relationship = 'Relationship Required';
                if (!d.nominee_dob) newErrors.nominee_dob = 'Nominee DOB Required';
                if (!d.nominee_phone) newErrors.nominee_phone = 'Nominee Phone Required';
                else if (!validatePhone(d.nominee_phone)) newErrors.nominee_phone = 'Invalid Nominee Phone';
            }
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleSave = async () => {
        const validationErrors = validateSection(editingSection);
        if (Object.keys(validationErrors).length > 0) {
            const errorList = Object.values(validationErrors);
            toast((t) => (
                <div className="flex flex-col gap-1.5 min-w-[200px]">
                    <div className="flex items-center gap-2 text-red-600 font-bold border-b border-red-100 pb-1 mb-1">
                        <FiAlertTriangle size={16} />
                        <span>Validation Errors</span>
                    </div>
                    <ul className="list-disc pl-4 text-[11px] text-gray-700 space-y-1">
                        {errorList.slice(0, 5).map((msg, i) => (
                            <li key={i} className="leading-tight">{msg}</li>
                        ))}
                        {errorList.length > 5 && (
                            <li className="text-gray-400 italic">...and {errorList.length - 5} more errors</li>
                        )}
                    </ul>
                    <div className="mt-2 text-[10px] text-gray-400 bg-gray-50 p-1.5 rounded text-center">
                        Please correct the highlighted fields above
                    </div>
                </div>
            ), {
                duration: 6000,
                icon: '⚠️',
                style: {
                    borderRadius: '12px',
                    background: '#fff',
                    color: '#333',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                }
            });
            return false;
        }

        let payload = JSON.parse(JSON.stringify(tempData)); // Deep copy to avoid mutation issues

        // Bank Details Logic
        if (editingSection === 'bank') {
            const oldLen = (data.bank_details || []).length;
            const newLen = (payload.bank_details || []).length;

            if (newLen > oldLen && newLen > 0) {
                const isMain = await new Promise(resolve => {
                    setBankConfirmModal({ open: true, resolve });
                });
                const lastIdx = newLen - 1;

                payload.bank_details.forEach((b, i) => {
                    if (isMain) {
                        if (i === lastIdx) {
                            b.is_active = true;
                            // b.expiry_date = null; // Removed: Should not clear user-entered or default expiry
                        } else {
                            b.is_active = false;
                            if (!b.expiry_date) b.expiry_date = new Date().toISOString().split('T')[0];
                        }
                    } else {
                        if (i === lastIdx) {
                            b.is_active = false;
                        }
                        // Keep existing active status for others? Or ensure one is main?
                        // If user clicked NO, we assume previous active account remains active.
                    }
                });
            } else if (newLen === 1) {
                // First account always active
                if (payload.bank_details[0]) payload.bank_details[0].is_active = true;
            }
        }

        const loadingToast = toast.loading('Saving changes...');
        try {
            await api.post('/employee/details', payload);
            setData(payload);
            setEditingSection(null);

            // Sync global user state if own profile image was updated
            if (payload.user_id === globalUser?.user_id && payload.profile_image) {
                // Determine the relative path for the global state (to match backend logic)
                let imgPath = payload.profile_image;
                const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL || '';
                if (imgPath.startsWith(IMAGE_BASE_URL)) {
                    imgPath = imgPath.replace(IMAGE_BASE_URL, '').replace(/^\/+/, '');
                }

                setGlobalUser(prev => ({
                    ...prev,
                    user_img: imgPath,
                    image: imgPath // Profile.jsx uses 'image', other places might use 'user_img'
                }));

                // Also update localStorage for persistence
                const localData = JSON.parse(localStorage.getItem('user') || '{}');
                localData.user_img = imgPath;
                localData.image = imgPath;
                localStorage.setItem('user', JSON.stringify(localData));
            }

            toast.success('Saved successfully', { id: loadingToast });
            return true;
        } catch (error) {
            console.error("Save Error", error);
            if (error.response && error.response.status === 422 && error.response.data.errors) {
                setErrors(error.response.data.errors);
                toast.error((t) => (
                    <div className="flex flex-col gap-1">
                        <span className="font-bold text-red-600">Server Validation Failed:</span>
                        <ul className="list-disc pl-4 text-xs text-gray-600 mt-1 space-y-0.5">
                            {Object.values(error.response.data.errors).slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                    </div>
                ), { id: loadingToast, duration: 5000 });
            } else {
                toast.error('Save failed. Please try again.', { id: loadingToast });
            }
            return false;
        }
    };

    const handleChange = (e) => {
        setTempData({ ...tempData, [e.target.name]: e.target.value });
    };

    const handleJobRoleChange = (e) => {
        const newRole = e.target.value;
        const currentRole = tempData.job_role;

        if (newRole === currentRole) return;

        // If history is empty, it's the first time - no popup
        if (!tempData.role_history || tempData.role_history.length === 0) {
            setTempData({
                ...tempData,
                job_role: newRole,
                role_history: [{
                    job_role: newRole,
                    start_date: tempData.joining_date || format(new Date(), 'yyyy-MM-dd'),
                    status: 'Active'
                }]
            });
        } else {
            // Second time or more - show popup
            // Calculate hypothetical prev role end date (Start Date - 1 day)
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            setPromotionModal({
                open: true,
                newRole: newRole,
                startDate: format(today, 'yyyy-MM-dd'),
                prevRoleEndDate: format(yesterday, 'yyyy-MM-dd'),
                prevRoleStartDate: tempData.role_history[tempData.role_history.length - 1]?.start_date || tempData.joining_date || format(today, 'yyyy-MM-dd')
            });
        }
    };

    const confirmPromotion = () => {
        const { newRole, startDate, prevRoleEndDate, prevRoleStartDate } = promotionModal;

        if (!startDate) {
            toast.error("Start Date is required");
            return;
        }

        const start = new Date(startDate);
        const pStart = new Date(prevRoleStartDate);
        const pEnd = prevRoleEndDate ? new Date(prevRoleEndDate) : null;

        // 1. Basic Sequence Validation
        if (pEnd && pEnd < pStart) {
            toast.error(`The previous role cannot end (${format(pEnd, 'dd/MM/yyyy')}) before it started (${format(pStart, 'dd/MM/yyyy')}). Please correct the Start or End date.`);
            return;
        }

        if (start < pStart) {
            toast.error(`The new role cannot start before the previous role started (${format(pStart, 'dd/MM/yyyy')}).`);
            return;
        }

        // 2. Joining Date Validation
        if (tempData.joining_date && pStart < new Date(tempData.joining_date)) {
            toast.error(`Previous role start date cannot be earlier than Joining Date (${format(new Date(tempData.joining_date), 'dd/MM/yyyy')})`);
            return;
        }

        const history = [...(tempData.role_history || [])];
        let updatedHistory = [];

        // Update Previous Role
        if (history.length > 0) {
            const lastRoleIndex = history.length - 1;
            const lastRole = { ...history[lastRoleIndex] };

            // Update the start date in case the user corrected it in the modal
            lastRole.start_date = prevRoleStartDate;

            if (prevRoleEndDate) {
                lastRole.end_date = prevRoleEndDate;
                lastRole.status = 'Completed';
            }

            updatedHistory = [...history.slice(0, lastRoleIndex), lastRole];
        } else {
            updatedHistory = [...history];
        }

        // Add New Role
        updatedHistory.push({
            job_role: newRole,
            start_date: startDate,
            end_date: '', // Active
            status: 'Active'
        });

        setTempData({
            ...tempData,
            job_role: newRole,
            role_history: updatedHistory,
            // Optimistically update designation for UI if needed immediately, though backend syncs it.
            // But data.designation comes from backend join. We can't easily update 'data' state for that field without refetch.
            // So we just rely on job_role update in UI.
        });
        setPromotionModal({ open: false, newRole: '', startDate: '', prevRoleEndDate: '' });
        toast.success(`Position updated to ${newRole}`);
    };

    const handleFamilyChange = (relation, field, value) => {
        setTempData(prev => ({
            ...prev,
            family_details: {
                ...prev.family_details,
                [relation]: {
                    ...prev.family_details[relation],
                    [field]: value
                }
            }
        }));
    };

    const handleDateChange = (name, date) => {
        setTempData({ ...tempData, [name]: date ? format(date, 'yyyy-MM-dd') : null });
    };

    const handleDeepChange = (field, index, subField, value) => {
        const list = [...(tempData[field] || [])];
        if (!list[index]) list[index] = {};
        list[index][subField] = value;
        setTempData({ ...tempData, [field]: list });
    };

    const addRow = (field, initialObj = {}) => {
        setTempData({ ...tempData, [field]: [...(tempData[field] || []), initialObj] });
    };

    const removeRow = (field, index) => {
        const list = [...(tempData[field] || [])];
        list.splice(index, 1);
        setTempData({ ...tempData, [field]: list });
    };

    const handleFileUpload = async (field, file) => {
        if (!file) return;
        const toastId = toast.loading('Uploading...');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/upload?user_id=${targetId}` : '/employee/upload';
            const res = await api.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                setTempData({ ...tempData, [field]: res.data.path });
                toast.success('Uploaded', { id: toastId });
            } else toast.error('Check file size/type', { id: toastId });
        } catch (e) { toast.error('Error', { id: toastId }); }
    };

    const handleDeepFileUpload = async (listName, index, subField, file, docType = null) => {
        if (!file) return;
        const toastId = toast.loading('Uploading...');
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (docType) formData.append('type', docType);

            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/upload?user_id=${targetId}` : '/employee/upload';
            const res = await api.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                handleDeepChange(listName, index, subField, res.data.path);
                toast.success('Uploaded', { id: toastId });
            } else toast.error('Failed', { id: toastId });
        } catch (e) { toast.error('Error', { id: toastId }); }
    };

    const getExperience = () => {
        if (!data.joining_date) return '-';
        const start = new Date(data.joining_date);
        const now = new Date();
        const years = differenceInYears(now, start);
        const months = differenceInMonths(now, start) % 12;
        return `${years} Years ${months} Months`;
    };

    const getBankColor = (bankName) => {
        const colors = {
            'HDFC Bank': 'from-blue-800 to-blue-600',
            'ICICI Bank': 'from-orange-700 to-orange-500',
            'State Bank of India': 'from-blue-500 to-cyan-400',
            'Axis Bank': 'from-red-800 to-red-600',
            'Kotak Mahindra Bank': 'from-red-700 to-red-500',
            'Bank of Baroda': 'from-orange-600 to-orange-400',
            'Punjab National Bank': 'from-yellow-700 to-yellow-500',
            'Union Bank of India': 'from-red-600 to-blue-600',
            'Canara Bank': 'from-blue-600 to-yellow-500',
            'IndusInd Bank': 'from-red-900 to-red-700',
            'Yes Bank': 'from-blue-700 to-red-500',
            'City Union Bank': 'from-red-500 to-orange-400',
            'Indian Bank': 'from-blue-900 to-yellow-600'
        };
        return colors[bankName] || 'from-slate-700 to-slate-500';
    };

    const getDurationText = (start, end) => {
        if (!start) return '';
        const endDate = end ? new Date(end) : new Date();
        const years = differenceInYears(endDate, new Date(start));
        const months = differenceInMonths(endDate, new Date(start)) % 12;
        return (years > 0 ? `${years} yrs ` : '') + (months + ' mos');
    };

    const preprocessImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Upscale 2x for much better OCR accuracy
                    canvas.width = img.width * 2;
                    canvas.height = img.height * 2;

                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    // Luminosity-based Binarization
                    for (let i = 0; i < data.length; i += 4) {
                        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                        // High contrast thresholding (black text < 120)
                        const final = gray < 130 ? 0 : 255;
                        data[i] = final;
                        data[i + 1] = final;
                        data[i + 2] = final;
                    }
                    ctx.putImageData(imageData, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleTesseractScan = async (type, file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', type);

            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/verify-identity?user_id=${targetId}` : '/employee/verify-identity';
            const uploadRes = await api.post(url, formData);
            if (!uploadRes.data.status) throw new Error("Upload Failed");
            if (!scannerActiveRef.current) return; // Stop if closed

            const fileUrl = uploadRes.data.file_url;
            const processedImage = await preprocessImage(file);
            if (!scannerActiveRef.current) return; // Stop if closed

            const { data: { text } } = await Tesseract.recognize(
                processedImage,
                'eng',
                {
                    logger: m => console.log(m),
                    tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/ '
                }
            );

            if (!scannerActiveRef.current) return; // Stop if closed
            console.log("OCR Raw Extracted Text:", text);

            let extractedNumber = '';
            const words = text.toUpperCase().split(/[\s\n]+/);

            if (type === 'PAN') {
                const panPattern = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/;
                for (let word of words) {
                    let cleaned = word.replace(/[^A-Z0-9]/g, '');
                    if (cleaned.length === 10) {
                        // Refined Repair Logic
                        let p1 = cleaned.substring(0, 5).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                        let p2 = cleaned.substring(5, 9).replace(/O/g, '0').replace(/I/g, '1').replace(/S/g, '5').replace(/B/g, '8');
                        let p3 = cleaned.substring(9, 10).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');

                        let repaired = p1 + p2 + p3;
                        if (panPattern.test(repaired)) {
                            extractedNumber = repaired;
                            break;
                        }
                    }
                }
            } else if (type === 'AADHAAR') {
                // Search for 12 digits, potentially with spaces
                const mergedText = text.replace(/\s/g, '');
                const aadhaarMatch = mergedText.match(/[0-9]{12}/);
                if (aadhaarMatch) {
                    extractedNumber = aadhaarMatch[0];
                } else {
                    const groupsMatch = text.match(/[0-9]{4}\s[0-9]{4}\s[0-9]{4}/);
                    if (groupsMatch) extractedNumber = groupsMatch[0].replace(/\s/g, '');
                }
            }

            // Fallback: Search in collapsed text (handles spaces/breaks in PAN)
            if (type === 'PAN' && !extractedNumber) {
                const panPattern = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/;
                const collapsed = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
                for (let i = 0; i <= collapsed.length - 10; i++) {
                    let potential = collapsed.substring(i, i + 10);
                    let p1 = potential.substring(0, 5).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                    let p2 = potential.substring(5, 9).replace(/O/g, '0').replace(/I/g, '1').replace(/S/g, '5').replace(/B/g, '8');
                    let p3 = potential.substring(9, 10).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                    let repaired = p1 + p2 + p3;
                    if (panPattern.test(repaired)) {
                        extractedNumber = repaired;
                        break;
                    }
                }
            }

            if (extractedNumber && scannerActiveRef.current) {
                setTempData(prev => ({
                    ...prev,
                    [type === 'PAN' ? 'pan_number' : 'aadhaar_number']: extractedNumber,
                    [type === 'PAN' ? 'pan_file' : 'aadhaar_file']: fileUrl,
                    [type === 'PAN' ? 'pan_local_filename' : 'aadhaar_local_filename']: file.name
                }));

                setScannedData(prev => ({
                    ...prev,
                    [type]: { number: extractedNumber }
                }));

                setVerificationStatus(prev => ({ ...prev, [type]: 'verified' }));

                setTimeout(() => {
                    if (scannerActiveRef.current) {
                        setIdentityModal({ open: false, type: null, file: null, preview: null });
                        scannerActiveRef.current = false;
                        toast.success(`${type} Number extracted!`, { icon: '✅' });
                    }
                }, 2000);
            } else if (scannerActiveRef.current) {
                setVerificationStatus(prev => ({ ...prev, [type]: 'error' }));
                toast.error(`Could not detect valid ${type} number. Please try a clearer image.`);
            }

        } catch (e) {
            console.error("OCR Error:", e);
            if (scannerActiveRef.current) {
                setVerificationStatus(prev => ({ ...prev, [type]: 'error' }));
                toast.error("Extraction failed. Please try again.");
            }
        }
    };

    const handleIdentityUpload = (type, file) => {
        if (!file) return;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            toast.error("Only JPG or PNG images are allowed for Identity verification.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setIdentityModal({ open: true, type, file, preview: e.target.result });
            setVerificationStatus(prev => ({ ...prev, [type]: 'scanning' }));
            scannerActiveRef.current = true;
            handleTesseractScan(type, file);
        };
        reader.readAsDataURL(file);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-6 font-sans text-sm text-gray-700">
            {cropperImage && (
                <ImageCropper
                    imageSrc={cropperImage}
                    onCancel={() => setCropperImage(null)}
                    onCropComplete={(blob) => {
                        setCropperImage(null);
                        const file = new File([blob], "profile_pic.jpg", { type: "image/jpeg" });
                        handleFileUpload('profile_image', file);
                    }}
                />
            )}
            <div className="max-w-6xl mx-auto">

                <div className="flex justify-between items-end mb-8 border-b pb-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight flex items-center gap-3">
                            <span className="bg-gradient-to-r from-red-600 to-red-800 text-transparent bg-clip-text">Employee Profile</span>
                            {isReadOnly && <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded border border-gray-300">Read Only</span>}
                        </h1>
                        <p className="text-gray-500 mt-1 flex items-center gap-2"> Manage and view detailed employee information safely. </p>
                    </div>
                    <div className="flex gap-3">
                        {getQueryParam('user_id') && (
                            <button onClick={() => navigate('/home?tab=employees')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition font-medium px-3 py-2 rounded hover:bg-gray-100">
                                <FiChevronLeft /> Back
                            </button>
                        )}
                        <button onClick={() => navigate('/org')} className="flex items-center gap-2 text-white bg-red-600 hover:bg-red-700 transition font-bold px-4 py-2 rounded-lg shadow-md shadow-purple-100">
                            <FiUsers /> Org Chart
                        </button>
                    </div>
                </div>

                <div id="employee-details-container">

                    {/* Promotion Confirmation Modal */}
                    {promotionModal.open && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100"
                            >
                                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                            <FiActivity size={20} />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">Confirm Promotion</h3>
                                    </div>
                                    <button onClick={() => setPromotionModal({ ...promotionModal, open: false })} className="text-gray-400 hover:text-gray-600 transition">
                                        <FiX size={20} />
                                    </button>
                                </div>
                                <div className="p-8 space-y-6">
                                    <div className="flex flex-col gap-2">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">New Designation</span>
                                        <div className="text-xl font-extrabold text-[#C30010] tracking-tight">{promotionModal.newRole}</div>
                                    </div>

                                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3">
                                        <FiCalendar className="text-blue-500 shrink-0 mt-0.5" size={18} />
                                        <div className="text-xs text-blue-700 leading-relaxed">
                                            The previous role will be marked as "Completed". Verify the end date below.
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <InputField
                                            label="Previous Role Start"
                                            type="date"
                                            value={promotionModal.prevRoleStartDate}
                                            onChange={(e) => setPromotionModal({ ...promotionModal, prevRoleStartDate: e.target.value })}
                                            required
                                        />
                                        <InputField
                                            label="Previous Role End"
                                            type="date"
                                            value={promotionModal.prevRoleEndDate}
                                            onChange={(e) => setPromotionModal({ ...promotionModal, prevRoleEndDate: e.target.value })}
                                            required
                                        />
                                        <div className="col-span-full">
                                            <InputField
                                                label="New Role Start Date"
                                                type="date"
                                                value={promotionModal.startDate}
                                                onChange={(e) => setPromotionModal({ ...promotionModal, startDate: e.target.value })}
                                                required
                                                minDate={tempData.joining_date ? new Date(tempData.joining_date) : null}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100 flex gap-2 sm:gap-3">
                                    <button
                                        onClick={() => setPromotionModal({ ...promotionModal, open: false })}
                                        className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmPromotion}
                                        className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-[#C30010] rounded-xl hover:bg-[#a0000d] shadow-lg shadow-red-100 transition flex items-center justify-center gap-1.5"
                                    >
                                        Set New Role <FiChevronRight className="hidden sm:block" />
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* Bank Activation Modal */}
                    {bankConfirmModal.open && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden border border-gray-100"
                            >
                                <div className="p-6 text-center">
                                    <div className="w-16 h-16 bg-red-50 text-[#C30010] rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FiCreditCard size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Activate Bank Account?</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed mb-6">
                                        Is this new bank account the <strong>CURRENT ACTIVE</strong> account? Only one account can be active at a time.
                                    </p>
                                    <div className="flex flex-col gap-2.5">
                                        <button
                                            onClick={() => {
                                                const resolve = bankConfirmModal.resolve;
                                                setBankConfirmModal({ open: false, resolve: null });
                                                resolve(true);
                                            }}
                                            className="w-full py-2.5 sm:py-3 bg-[#C30010] text-sm sm:text-base text-white rounded-xl font-bold shadow-lg shadow-red-100 hover:bg-[#a0000d] transition-all"
                                        >
                                            Yes, Set as Active
                                        </button>
                                        <button
                                            onClick={() => {
                                                const resolve = bankConfirmModal.resolve;
                                                setBankConfirmModal({ open: false, resolve: null });
                                                resolve(false);
                                            }}
                                            className="w-full py-2.5 sm:py-3 bg-gray-100 text-sm sm:text-base text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-all"
                                        >
                                            No, Keep as Inactive
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* Identity Verification Pop-up Modal */}
                    {identityModal.open && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-gray-100 overflow-hidden relative"
                            >
                                {/* Close Button */}
                                <button
                                    onClick={() => {
                                        setIdentityModal({ open: false, type: null, file: null, preview: null });
                                        scannerActiveRef.current = false;
                                        if (identityModal.type) {
                                            setVerificationStatus(prev => ({ ...prev, [identityModal.type]: 'idle' }));
                                        }
                                    }}
                                    className="absolute top-4 right-4 z-20 p-2 bg-white/80 backdrop-blur-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all shadow-sm border border-gray-100"
                                    title="Close Scanner"
                                >
                                    <FiX size={20} />
                                </button>

                                <div className="p-6">
                                    {/* Header */}
                                    <div className="flex justify-between items-center mb-6 pr-8">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-red-50 text-[#C30010] rounded-lg">
                                                {identityModal.type === 'PAN' ? <FiCreditCard size={24} /> : <FaIdCard size={24} />}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900">{identityModal.type} Verification</h3>
                                                <div className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded inline-block font-bold tracking-wide mt-1">GOVT. ISSUED ID</div>
                                            </div>
                                        </div>
                                        {verificationStatus[identityModal.type] === 'verified' && (
                                            <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                                <FiCheckCircle /> Verified
                                            </div>
                                        )}
                                    </div>

                                    {/* Scanning Area */}
                                    <div className="relative rounded-xl overflow-hidden border-2 border-slate-100 mb-6 bg-slate-50 min-h-[200px] max-h-[400px]">
                                        <img src={identityModal.preview} alt="ID Preview" className="w-full h-full object-contain" />

                                        {/* Scan Line Animation */}
                                        {verificationStatus[identityModal.type] === 'scanning' && (
                                            <div className="absolute inset-0 z-10 w-full h-full">
                                                <motion.div
                                                    className="w-full h-1.5 bg-gradient-to-r from-transparent via-[#C30010] to-transparent shadow-[0_0_20px_rgba(195,0,16,0.6)]"
                                                    animate={{ top: ['0%', '100%', '0%'] }}
                                                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                                                    style={{ position: 'absolute' }}
                                                />
                                                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-[10px] font-bold px-3 py-1 rounded-full backdrop-blur-md flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                                    SCANNING DOCUMENT...
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Extracted Data Fields */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1.5 tracking-wider">
                                                {identityModal.type} Number <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={scannedData[identityModal.type]?.number || ''}
                                                    readOnly
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-slate-700 font-mono font-bold tracking-widest text-lg"
                                                    placeholder="Scanning..."
                                                />
                                                {verificationStatus[identityModal.type] === 'verified' && <FiLock className="absolute right-4 top-4 text-green-500" />}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* 1. Personal & Family Details */}
                    <SectionCard
                        title="Personal Information"
                        icon={FiUser}
                        isEditing={editingSection === 'personal'}
                        onEdit={isReadOnly ? null : () => startEdit('personal')}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                    >
                        {editingSection === 'personal' ? (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                {/* Image */}
                                <div className="col-span-1 flex flex-col items-center">
                                    <div className={`w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed ${errors.profile_image ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-300'} flex items-center justify-center overflow-hidden relative group`}>
                                        {tempData.profile_image ? <img src={tempData.profile_image} className="w-full h-full object-cover" alt="Profile" /> : <FiUser size={40} className="text-gray-300" />}
                                        <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition">
                                            <FiUpload className="text-white" />
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const reader = new FileReader();
                                                        reader.onload = () => setCropperImage(reader.result);
                                                        reader.readAsDataURL(file);
                                                        e.target.value = null; // Reset input
                                                    }
                                                }}
                                                accept="image/*"
                                            />
                                        </label>
                                    </div>
                                    <span className="text-[10px] bg-red-50 text-[#C30010] px-2 py-0.5 rounded mt-2 font-bold uppercase tracking-wider">Upload Photo *</span>
                                    {errors.profile_image && <span className="text-[10px] text-red-500 font-bold mt-1 uppercase tracking-tighter animate-pulse">{errors.profile_image}</span>}
                                </div>

                                <div className="col-span-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    <InputField label="Employee Name (As per Aadhaar)" name="aadhaar_name" value={tempData.aadhaar_name} onChange={handleChange} required error={errors.aadhaar_name} />
                                    <InputField
                                        label="Job Role (Admin Only)"
                                        name="job_role"
                                        value={tempData.job_role}
                                        onChange={handleJobRoleChange}
                                        as="select"
                                        options={config.designations}
                                        required
                                        error={errors.job_role}
                                        disabled={!(globalUser && [1, 9].includes(Number(globalUser.role_id || globalUser.user_role_id)))}
                                    />
                                    {/* Admin-Only Official Job Role */}
                                    {globalUser && (Number(globalUser.role_id || globalUser.user_role_id) === 1) && (
                                        <div className="p-3 bg-red-50 border border-red-100 rounded-xl relative">
                                            <div className="absolute -top-2 left-3 bg-red-100 text-[#C30010] text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                                                Admin Only
                                            </div>
                                            <SearchableSelect
                                                label="Official / Admin Assigned Role"
                                                value={tempData.official_job_role}
                                                onChange={(val) => setTempData(prev => ({ ...prev, official_job_role: val }))}
                                                options={config.designations || []}
                                                placeholder="Select or Type Official Role"
                                            />
                                            <p className="text-[10px] text-gray-400 mt-1 italic">
                                                * This role will take precedence in Reports & ID Cards if set.
                                            </p>
                                        </div>
                                    )}
                                    <DatePickerField label="Date of Birth" selected={tempData.dob} onChange={(d) => handleDateChange('dob', d)} required error={errors.dob} />
                                    <InputField label="Blood Group" name="blood_group" value={tempData.blood_group} onChange={handleChange} options={['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']} as="select" required error={errors.blood_group} />
                                    <PhoneInput label="Personal Mobile" name="mobile" value={tempData.mobile} onChange={handleChange} required error={errors.mobile} />
                                    <InputField label="Personal Email" name="personal_email" value={tempData.personal_email} onChange={handleChange} required error={errors.personal_email} />
                                    <DatePickerField label="Joining Date (Enova)" selected={tempData.joining_date} onChange={(d) => handleDateChange('joining_date', d)} />
                                    <div className="col-span-full">
                                        <InputField label="Permanent Address" name="address_permanent" as="textarea" value={tempData.address_permanent} onChange={handleChange} required fullWidth error={errors.address_permanent} />
                                        <div className="flex items-center gap-2 mt-2 mb-1 pl-1">
                                            <input
                                                type="checkbox"
                                                id="sameAsPerm"
                                                className="w-4 h-4 text-[#C30010] border-gray-300 rounded focus:ring-[#C30010]"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTempData(prev => ({ ...prev, address_communication: prev.address_permanent }));
                                                    }
                                                }}
                                            />
                                            <label htmlFor="sameAsPerm" className="text-[11px] font-bold text-gray-500 uppercase cursor-pointer select-none">Same as Permanent Address</label>
                                        </div>
                                        <InputField label="Communication Address" name="address_communication" as="textarea" value={tempData.address_communication} onChange={handleChange} required fullWidth error={errors.address_communication} />
                                    </div>
                                </div>

                                {/* Family Details Grid */}
                                <div className="col-span-full border-t pt-4 mt-2">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-4">Family Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                        {['father', 'mother', 'spouse'].map(relation => (
                                            <div key={relation} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
                                                <h5 className="text-[11px] font-bold uppercase text-[#C30010] mb-3 tracking-widest flex items-center gap-2">
                                                    <div className="w-1 h-3 bg-[#C30010] rounded-full" />
                                                    {relation} Details
                                                </h5>
                                                <div className="space-y-3">
                                                    <InputField
                                                        label="Name"
                                                        value={tempData.family_details?.[relation]?.name}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                                            handleFamilyChange(relation, 'name', val);
                                                        }}
                                                        required={relation !== 'spouse'}
                                                        error={relation !== 'spouse' && errors[`${relation}_name`]}
                                                    />
                                                    <div className="flex flex-col gap-1.5">
                                                        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
                                                            Contact {relation !== 'spouse' && <span className="text-red-500 font-bold">*</span>}
                                                        </label>
                                                        <div className={`flex items-center border ${errors[relation + '_contact'] ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl bg-white focus-within:ring-2 focus-within:ring-[#C30010]/10 focus-within:border-[#C30010] overflow-hidden transition-all`}>
                                                            <div className="bg-gray-50 px-3 py-2 border-r border-gray-200 text-gray-700 text-[12.5px] font-bold flex items-center gap-2">
                                                                <span>🇮🇳</span> +91
                                                            </div>
                                                            <input
                                                                className="w-full px-3 py-2 outline-none text-[12.5px] font-bold text-gray-800 placeholder-gray-400 font-mono tracking-widest"
                                                                placeholder="Mobile Number"
                                                                value={tempData.family_details?.[relation]?.contact?.replace('+91 ', '') || ''}
                                                                onChange={(e) => {
                                                                    const val = e.target.value.replace(/\D/g, '');
                                                                    if (val.length <= 10) {
                                                                        if (val.length === 1 && !/^[6-9]/.test(val)) return;
                                                                        handleFamilyChange(relation, 'contact', val ? `+91 ${val}` : '');
                                                                    }
                                                                }}
                                                                maxLength={10}
                                                            />
                                                        </div>
                                                        {errors[relation + '_contact'] && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{errors[relation + '_contact']}</span>}
                                                    </div>
                                                    <InputField label="Profession" value={tempData.family_details?.[relation]?.profession} onChange={(e) => handleFamilyChange(relation, 'profession', e.target.value)} />
                                                    <InputField label="Position" value={tempData.family_details?.[relation]?.position} onChange={(e) => handleFamilyChange(relation, 'position', e.target.value)} />
                                                    <InputField label="Organisation" value={tempData.family_details?.[relation]?.organisation} onChange={(e) => handleFamilyChange(relation, 'organisation', e.target.value)} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="flex flex-col md:flex-row gap-6 mb-6">
                                    <div className="flex flex-col items-center md:items-start text-center md:text-left gap-3 shrink-0 mx-auto md:mx-0">
                                        <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden shadow-sm border border-gray-200">
                                            {data.profile_image ? <img src={data.profile_image} className="w-full h-full object-cover" /> : <FiUser className="w-full h-full p-6 text-gray-400" />}
                                        </div>
                                        <div className="flex flex-col items-center md:items-start">
                                            <div className="font-bold text-gray-800 text-sm">{data.designation || 'Designation'}</div>
                                            {data.reporting_manager && (
                                                <div className="text-[10px] text-gray-500 uppercase font-semibold mt-1">
                                                    Reports to: <span className="text-gray-700 normal-case">{data.reporting_manager}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 flex-1">
                                        <Field label="Full Name" value={data.aadhaar_name} />

                                        <div className={`flex flex-col mb-2`}>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Job Role</label>
                                            <div className="flex items-center gap-2 text-gray-800 font-medium text-sm">
                                                {(roleId === 1 && data.official_job_role) ? (
                                                    <div className="flex flex-col">
                                                        <span className="flex items-center gap-1.5 text-[#C30010] font-bold">
                                                            {data.official_job_role}
                                                            <span className="px-1.5 py-0.5 bg-red-100 text-[#C30010] text-[8px] rounded uppercase tracking-wider">Official</span>
                                                        </span>
                                                        {data.job_role && data.job_role !== data.official_job_role && (
                                                            <span className="text-[10px] text-gray-400 line-through decoration-gray-300">{data.job_role}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="truncate w-full">{data.job_role || '-'}</span>
                                                )}
                                            </div>
                                        </div>
                                        <Field label="DOB" value={data.dob ? format(new Date(data.dob), 'dd/MM/yyyy') : '-'} />
                                        <Field label="Blood Group" value={data.blood_group} />
                                        <Field label="Mobile" value={data.mobile} type="tel" icon={FiPhone} />
                                        <Field label="Email" value={data.personal_email} />
                                        <Field label="Joining Date" value={data.joining_date ? format(new Date(data.joining_date), 'dd/MM/yyyy') : '-'} />
                                        <Field label="Experience (Enova)" value={getExperience()} />
                                        <Field label="Permanent Address" value={data.address_permanent} fullWidth />
                                        <Field label="Communication Address" value={data.address_communication} fullWidth />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                                    {['father', 'mother', 'spouse'].map(r => (
                                        <div key={r} className="p-3 bg-gray-50 rounded border border-gray-100">
                                            <span className="text-[10px] font-bold uppercase text-gray-400 block mb-1">{r}</span>
                                            <div className="font-semibold text-gray-800">{data.family_details?.[r]?.name || '-'}</div>
                                            {data.family_details?.[r]?.contact ? (
                                                <a href={`tel:${data.family_details[r].contact.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 text-xs text-blue-600 font-bold mt-1 hover:text-blue-700 transition">
                                                    <FiPhone size={12} />
                                                    <span>{data.family_details[r].contact}</span>
                                                </a>
                                            ) : (
                                                <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium mt-1">
                                                    <FiPhone size={12} />
                                                    <span>-</span>
                                                </div>
                                            )}
                                            <div className="text-[11px] text-gray-500 mt-1">
                                                {data.family_details?.[r]?.profession ? `${data.family_details?.[r]?.profession} at ${data.family_details?.[r]?.organisation}` : ''}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </SectionCard>

                    {/* Role History / Experience Progression - Show only if 2+ roles exist */}
                    {((editingSection === 'experience' ||
                        (tempData.role_history || []).length > 1 ||
                        (data.role_history || []).length > 1)) && (
                            <SectionCard
                                title="Experience & Role Progression"
                                icon={FiActivity}
                                isEditing={editingSection === 'experience'}
                                onEdit={(isReadOnly || !(globalUser && [1, 9].includes(Number(globalUser.role_id || globalUser.user_role_id)))) ? null : () => startEdit('experience')}
                                onSave={handleSave}
                                onCancel={cancelEdit}
                            >
                                {editingSection === 'experience' ? (
                                    <div className="space-y-4">
                                        <p className="text-[10px] text-blue-600 font-bold bg-blue-50 p-2 rounded border border-blue-100 uppercase tracking-widest">
                                            💡 Tip: Adding a new role will automatically set the end date of the previous role.
                                        </p>
                                        <div className="space-y-3">
                                            {(tempData.role_history || []).map((item, idx) => (
                                                <div key={idx} className="flex flex-col md:flex-row items-end gap-3 p-3 bg-gray-50 border border-gray-200 rounded relative">
                                                    <div className="flex-1 w-full md:w-auto">
                                                        <InputField
                                                            label="Job Role Title"
                                                            value={item.job_role}
                                                            onChange={(e) => handleDeepChange('role_history', idx, 'job_role', e.target.value)}
                                                            required
                                                            placeholder="e.g. Senior Developer"
                                                            error={errors[`role_${idx}_name`]}
                                                        />
                                                    </div>
                                                    <div className="w-full md:w-36 shrink-0">
                                                        <InputField
                                                            label="Start Date"
                                                            type="date"
                                                            value={item.start_date}
                                                            onChange={(e) => handleDeepChange('role_history', idx, 'start_date', e.target.value)}
                                                            required
                                                            error={errors[`role_${idx}_start`]}
                                                        />
                                                    </div>
                                                    <div className="w-full md:w-36 shrink-0">
                                                        <InputField
                                                            label="End Date"
                                                            type="date"
                                                            value={item.end_date}
                                                            onChange={(e) => handleDeepChange('role_history', idx, 'end_date', e.target.value)}
                                                            placeholder="Present"
                                                            minDate={item.start_date ? new Date(item.start_date) : null}
                                                            error={errors[`role_${idx}_end`]}
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => removeRow('role_history', idx)}
                                                        className="mb-1.5 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                                        title="Remove"
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => addRow('role_history', {
                                                job_role: '',
                                                start_date: format(new Date(), 'yyyy-MM-dd'),
                                                end_date: '',
                                                status: 'Active'
                                            })}
                                            className="px-4 py-2 bg-white border border-dashed border-gray-300 rounded text-xs hover:border-blue-500 hover:text-blue-600 transition flex items-center gap-2 w-fit shadow-sm"
                                        >
                                            <FiPlus /> Add Promotion / New Role
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative pl-8 space-y-6 before:absolute before:inset-0 before:ml-3.5 before:h-full before:w-0.5 before:bg-gray-200">
                                        {(data.role_history || []).length > 0 ? (
                                            [...(data.role_history || [])].reverse().map((role, idx) => {
                                                const isLatest = idx === 0; // Since we reversed it, 0 is latest
                                                const isActive = isLatest && !role.end_date;

                                                return (
                                                    <div key={idx} className="relative flex items-center justify-between group">
                                                        {/* Timeline Node */}
                                                        <div className={`absolute -left-8 flex items-center justify-center w-7 h-7 bg-white border-2 ${isActive ? 'border-[#C30010]' : 'border-gray-300'} rounded-full shadow-sm z-10 group-hover:scale-110 transition-transform`}>
                                                            <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#C30010] animate-pulse' : 'bg-gray-400'}`}></div>
                                                        </div>

                                                        {/* Role Card */}
                                                        <div className={`flex-1 p-4 rounded-lg border ${isActive ? 'bg-white border-[#C30010]/20 shadow-md ring-1 ring-[#C30010]/10' : 'bg-gray-50 border-gray-100 shadow-sm'} transition-all ml-4`}>
                                                            <div className="flex justify-between items-start">
                                                                <div>
                                                                    <h4 className={`font-bold text-sm ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>{role.job_role}</h4>
                                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">eNova Software and Hardware Solutions</p>
                                                                </div>
                                                                {isActive && (
                                                                    <span className="px-2 py-0.5 bg-[#ffebee] text-[#C30010] text-[9px] font-bold rounded uppercase tracking-tighter border border-red-100 shadow-sm">Current Position</span>
                                                                )}
                                                            </div>

                                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-[11px] text-gray-500 font-bold uppercase tracking-wide">
                                                                <div className="flex items-center gap-1.5">
                                                                    <FiCalendar className="text-[#C30010]" size={13} />
                                                                    <span>
                                                                        {role.start_date ? format(new Date(role.start_date), 'MMM yyyy') : '-'} — {role.end_date ? format(new Date(role.end_date), 'MMM yyyy') : 'Present'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                                                    <FiActivity className="text-gray-400" size={13} />
                                                                    <span>{role.duration_text || getDurationText(role.start_date, role.end_date)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <div className="text-gray-400 text-xs italic py-4">No career progression history found. Use the edit button to add your first role.</div>
                                        )}

                                        {/* Total Experience Badge */}
                                        {data.total_experience_text && (
                                            <div className="mt-4 pt-4 border-t flex items-center justify-between">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Corporate Tenure</span>
                                                <span className="text-xs font-bold text-gray-800 bg-white border px-3 py-1 rounded-full shadow-sm">{data.total_experience_text}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </SectionCard>
                        )}



                    {/* 2. Educational & Certificates */}
                    <SectionCard
                        title="Educational Qualification"
                        icon={FiBook}
                        isEditing={editingSection === 'academic'}
                        onEdit={isReadOnly ? null : () => startEdit('academic')}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                    >
                        {editingSection === 'academic' ? (
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    {(tempData.academic_details || []).map((item, idx) => (
                                        <div key={idx} className="flex flex-col md:flex-row items-start md:items-end gap-3 p-3 bg-gray-50 border border-gray-200 rounded relative">
                                            {/* 1. Qualification Level */}
                                            <div className="w-full md:w-28 shrink-0">
                                                <SearchableSelect
                                                    label="Level"
                                                    value={item.qualification}
                                                    onChange={(val) => handleDeepChange('academic_details', idx, 'qualification', val)}
                                                    options={['10th', '12th', 'Diploma', 'UG', 'PG', 'PhD']}
                                                    required
                                                />
                                            </div>

                                            {/* 2. Board/Degree & Specialization */}
                                            {['10th', '12th'].includes(item.qualification) ? (
                                                <div className="w-full md:w-40 shrink-0">
                                                    <SearchableSelect
                                                        label="Board / Stream"
                                                        value={item.board}
                                                        onChange={(val) => handleDeepChange('academic_details', idx, 'board', val)}
                                                        options={config.boards || []}
                                                        required
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-full md:w-24 shrink-0">
                                                        <SearchableSelect
                                                            label="Degree"
                                                            value={item.degree}
                                                            onChange={(val) => handleDeepChange('academic_details', idx, 'degree', val)}
                                                            options={config.degrees_map?.[item.qualification] || config.degrees || []}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="w-full md:w-32 shrink-0">
                                                        <SearchableSelect
                                                            label="Specialization"
                                                            value={item.specialization}
                                                            onChange={(val) => handleDeepChange('academic_details', idx, 'specialization', val)}
                                                            options={[]}
                                                            placeholder="Ex: CSE"
                                                            required
                                                            hideIcon={true}
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {/* 3. Institute (Searchable for College, Text for School) */}
                                            <div className="w-full md:flex-1 min-w-[140px]">
                                                {['10th', '12th'].includes(item.qualification) ? (
                                                    <InputField
                                                        label="School Name"
                                                        value={item.institute}
                                                        onChange={(e) => handleDeepChange('academic_details', idx, 'institute', e.target.value)}
                                                        required
                                                        placeholder="Enter School Name"
                                                    />
                                                ) : (
                                                    <SearchableSelect
                                                        label="College / Institute"
                                                        value={item.institute}
                                                        onChange={(val) => handleDeepChange('academic_details', idx, 'institute', val)}
                                                        options={config.colleges}
                                                        required
                                                        placeholder="Select College..."
                                                    />
                                                )}
                                            </div>

                                            {/* 4. Year (Select Dropdown) */}
                                            <div className="w-full md:w-24 shrink-0">
                                                <InputField
                                                    label="Passed Out"
                                                    as="select"
                                                    value={item.year}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        const dobYear = data.dob ? new Date(data.dob).getFullYear() : null;
                                                        if (dobYear && val && parseInt(val) < dobYear + 12) {
                                                            toast.error(`Year too early for DOB (${dobYear})`);
                                                        }
                                                        handleDeepChange('academic_details', idx, 'year', val);
                                                    }}
                                                    options={Array.from({ length: 60 }, (_, i) => String(new Date().getFullYear() - i))}
                                                    required
                                                    error={errors[`ac_year_${idx}`]}
                                                />
                                            </div>

                                            <div className="w-full md:w-28 shrink-0 relative">
                                                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 mb-1.5 block">
                                                    Score <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative flex items-center">
                                                    <input
                                                        type="number"
                                                        value={item.percentage}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            const max = item.score_type === 'CGPA' ? 10 : 100;

                                                            // Allow empty string to let user clear input
                                                            if (val === '') {
                                                                handleDeepChange('academic_details', idx, 'percentage', val);
                                                                return;
                                                            }

                                                            const num = parseFloat(val);
                                                            // Strict check: must be a number, non-negative, and within range
                                                            if (!isNaN(num) && num >= 0 && num <= max) {
                                                                handleDeepChange('academic_details', idx, 'percentage', val);
                                                            }
                                                        }}
                                                        onFocus={() => setFocusedScore(idx)}
                                                        onBlur={() => setTimeout(() => setFocusedScore(null), 200)} // Delay to allow click
                                                        className={`w-full px-3 py-2 bg-white border ${errors[`ac_score_${idx}`] ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold text-gray-800 transition-all pr-12`}
                                                        placeholder={item.score_type === 'CGPA' ? 'Ex: 9.5' : 'Ex: 95'}
                                                        min="0"
                                                        max={item.score_type === 'CGPA' ? 10 : 100}
                                                        step={item.score_type === 'CGPA' ? "0.01" : "0.1"}
                                                    />
                                                    <div className="absolute right-1 flex items-center">
                                                        <button
                                                            onClick={() => handleDeepChange('academic_details', idx, 'score_type', item.score_type === 'CGPA' ? 'Percentage' : 'CGPA')}
                                                            className="flex items-center justify-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-[10px] font-bold text-gray-700 rounded-lg transition border border-gray-200 h-[24px]"
                                                            title="Toggle Unit"
                                                        >
                                                            {item.score_type === 'CGPA' ? 'CGPA' : '%'}
                                                        </button>
                                                    </div>

                                                    {/* Tooltip for Toggle */}
                                                    {focusedScore === idx && (
                                                        <div className="absolute -top-8 right-0 bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-lg z-10 animate-fade-in pointer-events-none whitespace-nowrap">
                                                            Select % or CGPA
                                                            <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
                                                        </div>
                                                    )}
                                                </div>
                                                {errors[`ac_score_${idx}`] && <span className="text-[9px] text-red-500 font-semibold block mt-1 leading-none animate-pulse">{errors[`ac_score_${idx}`]}</span>}
                                            </div>

                                            {/* 6. Actions (Upload, View, Delete) */}
                                            <div className="w-full md:w-auto shrink-0 pb-1 flex items-center gap-3">
                                                {/* Upload Button */}
                                                <label className={`cursor-pointer inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-[34px] sm:h-[38px] border-2 rounded-xl transition-all w-full md:w-auto ${item.file ? 'bg-red-50 border-red-100 text-[#C30010]' : 'bg-red-50 border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} ${errors[`ac_file_${idx}`] ? 'border-red-500 ring-1 ring-red-50' : ''}`} title={item.file ? "Change Document" : "Upload Document"}>
                                                    <FiUpload size={14} className={`sm:size-[16px] ${errors[`ac_file_${idx}`] ? 'text-red-500' : 'text-[#C30010]'}`} />
                                                    <span className={`font-bold text-[9px] sm:text-[10px] uppercase tracking-widest ${errors[`ac_file_${idx}`] ? 'text-red-600' : 'text-[#C30010]'}`}>
                                                        {item.file ? "Uploaded" : "Upload File *"}
                                                    </span>
                                                    <input type="file" className="hidden" onChange={(e) => handleDeepFileUpload('academic_details', idx, 'file', e.target.files[0])} accept="image/*,application/pdf" />
                                                </label>

                                                {/* View Button */}
                                                {item.file && (
                                                    <a
                                                        href={item.file}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="h-[34px] sm:h-[38px] w-[34px] sm:w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-[#C30010] hover:bg-[#ffebee] transition-all shadow-sm"
                                                        title="View Document"
                                                    >
                                                        <FiEye size={16} className="sm:size-[18px]" />
                                                    </a>
                                                )}

                                                {/* Delete Button */}
                                                <button
                                                    onClick={() => removeRow('academic_details', idx)}
                                                    className="h-[34px] sm:h-[38px] w-[34px] sm:w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-400 hover:text-white hover:bg-[#C30010] hover:border-[#C30010] transition-all shadow-sm shrink-0"
                                                    title="Remove"
                                                >
                                                    <FiTrash2 size={16} className="sm:size-[18px]" />
                                                </button>
                                            </div>

                                            {/* Mobile Remove Button */}
                                            <button
                                                onClick={() => removeRow('academic_details', idx)}
                                                className="md:hidden w-full py-2 text-red-500 bg-red-50 rounded border border-red-100 flex items-center justify-center gap-2 text-sm font-semibold mt-2"
                                            >
                                                <FiTrash2 /> Remove Qualification
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => addRow('academic_details', {})} className="px-4 sm:px-6 py-2 sm:py-3 bg-white border-2 border-dashed border-gray-200 rounded-xl text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-2 w-fit shadow-sm">
                                    <FiPlus size={14} className="sm:size-[16px]" /> Add Qualification
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(data.academic_details || []).map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center p-3 border-l-4 border-[#C30010] bg-gray-50">
                                        <div>
                                            <div className="font-bold text-gray-800">
                                                {item.qualification} - {item.degree || item.board}
                                                {item.specialization && <span className="text-gray-600 font-medium ml-1">({item.specialization})</span>}
                                            </div>
                                            {/* Display School/College Name */}
                                            <div className="text-xs font-semibold text-gray-600 mb-0.5">{item.institute}</div>
                                            <div className="text-xs text-gray-500">Passed: {item.year} • Score: {item.percentage} {item.score_type === 'CGPA' ? '(CGPA)' : '%'}</div>
                                        </div>
                                        {item.file && <a href={item.file} target="_blank" className="text-xs bg-white border border-red-100 px-2 py-1 rounded text-[#C30010] hover:bg-[#ffebee] transition">View Cert</a>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </SectionCard>

                    {/* New Section: Certificates & Awards */}
                    <SectionCard
                        title={
                            <div className="flex items-center gap-2">
                                <span>Certificates & Awards</span>
                                {/* Star Badge for Gamification */}
                                {(data.certificates || []).length > 2 && (
                                    <FiStar className="text-yellow-500" size={20} fill="currentColor" />
                                )}
                            </div>
                        }
                        icon={FiAward}
                        isEditing={editingSection === 'certificates'}
                        onEdit={isReadOnly ? null : () => startEdit('certificates')}
                        onSave={async () => {
                            if (await handleSave()) {
                                // Gamification Popup
                                if ((tempData.certificates || []).length > 2) {
                                    toast((t) => (
                                        <div className="flex items-center gap-3">
                                            <div className="bg-yellow-100 p-2 rounded-full"><FiStar className="text-yellow-600" size={24} fill="currentColor" /></div>
                                            <div>
                                                <b className="block text-gray-800">Well Done {data.aadhaar_name?.split(' ')[0]}!</b>
                                                <span className="text-xs text-text-500">You've added {(tempData.certificates || []).length} achievements. Keep it up!</span>
                                            </div>
                                            <button onClick={() => toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600"><FiX /></button>
                                        </div>
                                    ), { duration: 5000, position: 'top-center' });
                                }
                            }
                        }}
                        onCancel={cancelEdit}
                    >
                        {editingSection === 'certificates' ? (
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    {(tempData.certificates || []).map((item, idx) => (
                                        <div key={idx} className="flex flex-col md:flex-row items-start md:items-end gap-2 p-2 bg-gray-50 border border-gray-200 rounded relative">
                                            {/* 1. Type */}
                                            <div className="w-full md:w-28 shrink-0">
                                                <InputField
                                                    label="Type"
                                                    value={item.type}
                                                    onChange={(e) => handleDeepChange('certificates', idx, 'type', e.target.value)}
                                                    options={['Certificate', 'Award']}
                                                    as="select"
                                                    required
                                                    error={errors[`cert_${idx}_type`]}
                                                />
                                            </div>

                                            {/* 2. Title */}
                                            <div className="w-full md:flex-1 md:max-w-[260px] shrink-0">
                                                <InputField
                                                    label="Title / Name"
                                                    value={item.title}
                                                    onChange={(e) => handleDeepChange('certificates', idx, 'title', e.target.value)}
                                                    required
                                                    error={errors[`cert_${idx}_title`]}
                                                    placeholder="Title"
                                                />
                                            </div>

                                            {/* 3. Issuing Authority (New Field) */}
                                            <div className="w-full md:w-44 shrink-0">
                                                <InputField
                                                    label="Issuing Authority"
                                                    value={item.issuing_authority}
                                                    onChange={(e) => handleDeepChange('certificates', idx, 'issuing_authority', e.target.value)}
                                                    required
                                                    error={errors[`cert_${idx}_authority`]}
                                                    placeholder="Authority"
                                                />
                                            </div>

                                            {/* 4. Issue Date */}
                                            {/* 4. Issue Date */}
                                            <div className="w-full md:w-36 shrink-0">
                                                <InputField
                                                    label="Issue Date"
                                                    type="date"
                                                    value={item.issue_date}
                                                    onChange={(e) => handleDeepChange('certificates', idx, 'issue_date', e.target.value)}
                                                    required
                                                    error={errors[`cert_${idx}_issue`]}
                                                    maxDate={item.expiry_date && item.validity_type === 'Renewable' ? new Date(Math.min(new Date(), new Date(item.expiry_date))) : new Date()}
                                                />
                                            </div>

                                            {/* 5. Validity Type (Hidden for Awards) */}
                                            {item.type !== 'Award' && (
                                                <div className="w-full md:w-32 shrink-0">
                                                    <InputField
                                                        label="Validity"
                                                        value={item.validity_type}
                                                        onChange={(e) => {
                                                            handleDeepChange('certificates', idx, 'validity_type', e.target.value);
                                                            if (e.target.value === 'Perpetual') {
                                                                handleDeepChange('certificates', idx, 'expiry_date', '');
                                                            }
                                                        }}
                                                        options={['Perpetual', 'Renewable']}
                                                        as="select"
                                                        required
                                                        error={errors[`cert_${idx}_validity`]}
                                                    />
                                                </div>
                                            )}

                                            {/* 6. Expiry Date (Conditional: Certificate + Renewable) */}
                                            {item.type !== 'Award' && item.validity_type === 'Renewable' && (
                                                <div className="w-full md:w-36 shrink-0">
                                                    <InputField
                                                        label="Expiry Date"
                                                        type="date"
                                                        value={item.expiry_date}
                                                        onChange={(e) => handleDeepChange('certificates', idx, 'expiry_date', e.target.value)}
                                                        required
                                                        error={errors[`cert_${idx}_expiry`]}
                                                        minDate={item.issue_date ? new Date(item.issue_date) : null}
                                                    />
                                                </div>
                                            )}

                                            {/* 7. Actions: Upload, View, Delete */}
                                            <div className="flex items-center gap-3 w-full md:w-auto shrink-0 pb-1">
                                                {/* Upload */}
                                                <div className="flex flex-col gap-1 items-center">
                                                    <label className="text-[10px] font-bold text-gray-600 uppercase tracking-widest pl-1 md:invisible h-0 md:h-auto overflow-hidden">ACT</label>
                                                    <label className={`cursor-pointer inline-flex items-center justify-center gap-2 px-3 h-[38px] border-2 rounded-xl transition-all max-w-[160px] ${item.file ? 'bg-red-50 border-red-100 text-[#C30010]' : 'bg-red-50 border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} ${errors[`cert_${idx}_file`] ? 'border-red-500 ring-1 ring-red-50' : ''}`} title={item.file ? (typeof item.file === 'string' ? item.file.split('/').pop() : item.file.name) : "Upload"}>
                                                        <FiUpload className={`${item.file ? "text-[#C30010]" : "text-[#C30010]"} ${errors[`cert_${idx}_file`] ? 'text-red-500' : ''}`} size={16} />
                                                        <span className={`truncate max-w-[100px] text-[10px] font-bold uppercase tracking-wider ${item.file ? "text-[#C30010]" : "text-[#C30010]"} ${errors[`cert_${idx}_file`] ? 'text-red-600' : ''}`}>
                                                            {item.file ? (typeof item.file === 'string' ? item.file.split('/').pop() : item.file.name) : "Certificate *"}
                                                        </span>
                                                        <input type="file" className="hidden" onChange={(e) => handleDeepFileUpload('certificates', idx, 'file', e.target.files[0])} accept="image/*,application/pdf" />
                                                    </label>
                                                </div>

                                                {/* View (Eye) */}
                                                {item.file && (
                                                    <a href={item.file} target="_blank" rel="noopener noreferrer" className="h-[38px] w-[38px] flex items-center justify-center bg-white text-[#C30010] border border-gray-200 rounded-xl hover:bg-[#ffebee] transition-all shadow-sm mt-auto" title="View Document">
                                                        <FiEye size={18} />
                                                    </a>
                                                )}

                                                {/* Delete */}
                                                <button
                                                    onClick={() => removeRow('certificates', idx)}
                                                    className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-500 hover:text-white hover:bg-[#C30010] hover:border-[#C30010] transition-all shadow-sm mt-auto"
                                                    title="Remove"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => removeRow('certificates', idx)}
                                                className="md:hidden w-full py-2 text-red-500 bg-red-50 rounded border border-red-100 flex items-center justify-center gap-2 text-sm font-semibold mt-2"
                                            >
                                                <FiTrash2 /> Remove Entry
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => addRow('certificates', { type: 'Certificate', title: '', issue_date: '', expiry_date: '', file: '' })} className="px-4 sm:px-6 py-2 sm:py-3 bg-white border-2 border-dashed border-gray-200 rounded-xl text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-2 w-fit shadow-sm">
                                    <FiPlus size={14} className="sm:size-[16px]" /> Add New Entry
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(data.certificates || []).map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg bg-white shadow-sm hover:shadow-md transition group relative overflow-hidden">
                                        <div className={`p-2 rounded-full shrink-0 ${item.type === 'Award' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-[#C30010]'}`}>
                                            {item.type === 'Award' ? <FiAward size={20} /> : <FiFileText size={20} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h5 className="font-bold text-gray-800 text-sm truncate" title={item.title}>{item.title}</h5>
                                            {item.issuing_authority && <p className="text-xs text-gray-600 font-medium">{item.issuing_authority}</p>}

                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-gray-500 font-semibold uppercase tracking-wider">
                                                <span>{item.type}</span>
                                                <span>•</span>
                                                <span>Issued: {item.issue_date}</span>
                                                {item.validity_type && (
                                                    <>
                                                        <span>•</span>
                                                        <span className={item.validity_type === 'Perpetual' ? 'text-green-600' : 'text-[#C30010]'}>{item.validity_type}</span>
                                                    </>
                                                )}
                                            </div>

                                            {item.validity_type === 'Renewable' && item.expiry_date && (
                                                <div className="mt-1 flex items-center gap-2">
                                                    <p className={`text-[10px] font-bold ${new Date(item.expiry_date) < new Date() ? 'text-red-500' : 'text-gray-500'}`}>
                                                        {new Date(item.expiry_date) < new Date() ? 'EXPIRED' : 'Expires'}: {item.expiry_date}
                                                    </p>
                                                    {new Date(item.expiry_date) < new Date() && (
                                                        <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200 uppercase tracking-tight font-bold">Renewal Required</span>
                                                    )}
                                                </div>
                                            )}

                                            {item.file && <a href={item.file} target="_blank" className="text-[10px] text-[#C30010] hover:underline flex items-center gap-1 mt-2 font-bold uppercase tracking-wider"><FiCheckCircle size={10} /> View Document</a>}
                                        </div>
                                        {/* Status Line */}
                                        {item.validity_type === 'Renewable' && new Date(item.expiry_date) < new Date() && (
                                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500"></div>
                                        )}
                                    </div>
                                ))}
                                {(data.certificates || []).length === 0 && <div className="text-gray-400 text-xs italic col-span-full">No achievements added yet.</div>}
                            </div>
                        )}
                    </SectionCard >

                    {/* 2.5 Skill Set */}
                    <SectionCard
                        title="Professional Skill Set"
                        icon={FiCode}
                        isEditing={editingSection === 'skills'}
                        onEdit={isReadOnly ? null : () => startEdit('skills')}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                    >
                        {editingSection === 'skills' ? (
                            <div className="space-y-4">
                                <div className="space-y-3">
                                    {(tempData.skills || []).map((item, idx) => (
                                        <div key={idx} className="flex flex-col md:flex-row items-end gap-3 p-3 bg-gray-50 border border-gray-200 rounded relative">
                                            <div className="w-full md:w-48 shrink-0">
                                                <InputField
                                                    label="Category"
                                                    value={item.category}
                                                    onChange={(e) => handleDeepChange('skills', idx, 'category', e.target.value)}
                                                    options={config.skill_categories && config.skill_categories.length > 0 ? config.skill_categories : ['Technical', 'Non-Technical', 'Tools', 'Languages']}
                                                    as="select"
                                                    required
                                                />
                                            </div>
                                            <div className="flex-1 w-full md:w-auto">
                                                <InputField
                                                    label="Skill Name"
                                                    value={item.skill}
                                                    onChange={(e) => handleDeepChange('skills', idx, 'skill', e.target.value)}
                                                    placeholder="Ex: React, Photoshop, Leadership"
                                                    required
                                                />
                                            </div>
                                            <div className="w-full md:w-32 shrink-0">
                                                <InputField
                                                    label="Proficiency"
                                                    value={item.proficiency}
                                                    onChange={(e) => handleDeepChange('skills', idx, 'proficiency', e.target.value)}
                                                    options={['Beginner', 'Intermediate', 'Advanced', 'Expert']}
                                                    as="select"
                                                    required
                                                />
                                            </div>
                                            <button
                                                onClick={() => removeRow('skills', idx)}
                                                className="mb-1.5 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                                title="Remove"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => addRow('skills', { category: 'Technical', skill: '', proficiency: 'Intermediate' })} className="px-4 sm:px-6 py-2 sm:py-3 bg-white border-2 border-dashed border-gray-200 rounded-xl text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-2 w-fit shadow-sm">
                                    <FiPlus size={14} className="sm:size-[16px]" /> Add New Skill
                                </button>
                            </div>
                        ) : (
                            <div>
                                {(data.skills || []).length > 0 ? (
                                    <div className="flex flex-wrap gap-4">
                                        {['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills', 'Other'].map(cat => {
                                            const catSkills = (data.skills || []).filter(s => s.category === cat || (!['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills'].includes(s.category) && cat === 'Other' && !['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills'].includes(s.category)));
                                            if (catSkills.length === 0) return null;
                                            return (
                                                <div key={cat} className="flex-1 min-w-[200px]">
                                                    <h6 className="text-[10px] uppercase font-bold text-gray-400 mb-2">{cat}</h6>
                                                    <div className="flex flex-wrap gap-2">
                                                        {catSkills.map((s, i) => (
                                                            <div key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2 shadow-sm">
                                                                <span className="font-semibold text-gray-700 text-xs">{s.skill}</span>
                                                                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${s.proficiency === 'Expert' ? 'bg-[#C30010] text-white shadow-sm shadow-red-100' : s.proficiency === 'Advanced' ? 'bg-[#ffebee] text-[#C30010] border border-red-100' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                                                    {s.proficiency}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {/* Fallback for undefined categories or customized ones if config changes */}
                                        {(() => {
                                            const predefined = ['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills', 'Other'];
                                            const customCats = [...new Set((data.skills || []).map(s => s.category).filter(c => !predefined.includes(c)))];
                                            return customCats.map(cat => (
                                                <div key={cat} className="flex-1 min-w-[200px]">
                                                    <h6 className="text-[10px] uppercase font-bold text-gray-400 mb-2">{cat}</h6>
                                                    <div className="flex flex-wrap gap-2">
                                                        {(data.skills || []).filter(s => s.category === cat).map((s, i) => (
                                                            <div key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2 shadow-sm">
                                                                <span className="font-semibold text-gray-700 text-xs">{s.skill}</span>
                                                                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${s.proficiency === 'Expert' ? 'bg-[#C30010] text-white shadow-sm shadow-red-100' : s.proficiency === 'Advanced' ? 'bg-[#ffebee] text-[#C30010] border border-red-100' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                                                    {s.proficiency}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 text-xs italic">No skills added yet.</div>
                                )}
                            </div>
                        )}
                    </SectionCard>

                    {/* 3. Identity Proofs (With OCR Integration) */}
                    <SectionCard
                        title="Identity Proofs (KYC) (ONLY IMAGE IN PNG, JPG)"
                        icon={FiShield}
                        isEditing={editingSection === 'identity'}
                        onEdit={isReadOnly ? null : () => startEdit('identity')}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                    >
                        {editingSection === 'identity' ? (
                            <div className="space-y-6">
                                {/* Single Row UI for Real-time OCR */}
                                <div className="flex flex-col md:flex-row items-stretch md:items-end gap-5 md:gap-6 p-4 md:p-6 bg-gray-50/50 border border-gray-100 rounded-xl">

                                    {/* 1. Dropdown */}
                                    <div className="w-full md:w-64">
                                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-1 pl-1">Proof Type <span className="text-red-500 font-bold">*</span></label>
                                        <div className="relative">
                                            <select
                                                value={selectedIdType}
                                                onChange={(e) => setSelectedIdType(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-800 outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] appearance-none cursor-pointer transition-all pr-10"
                                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1rem' }}
                                            >
                                                <option value="">Select ID Type...</option>
                                                <option value="AADHAAR">Aadhaar</option>
                                                <option value="PAN">PAN</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* 2. Number Input */}
                                    <div className="w-full md:w-64">
                                        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest mb-1.5 flex items-center gap-1 pl-1">Proof Number <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={selectedIdType === 'PAN' ? (tempData.pan_number || '') : selectedIdType === 'AADHAAR' ? (tempData.aadhaar_number || '') : ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (selectedIdType === 'PAN') handleChange({ target: { name: 'pan_number', value: val } });
                                                    if (selectedIdType === 'AADHAAR') handleChange({ target: { name: 'aadhaar_number', value: val } });
                                                }}
                                                placeholder={selectedIdType ? `Enter ${selectedIdType} Number` : 'Select Type first'}
                                                disabled={!selectedIdType || verificationStatus[selectedIdType] === 'verified'}
                                                className={`w-full px-4 py-2 bg-white border ${errors.aadhaar_number || errors.pan_number ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold uppercase tracking-widest text-gray-800 placeholder-gray-400 transition-all font-mono disabled:bg-gray-50 disabled:text-gray-400`}
                                            />
                                            {selectedIdType && verificationStatus[selectedIdType] === 'verified' && (
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-sm" title="Verified">
                                                    <FiCheckCircle size={12} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 3. Upload & Actions */}
                                    <div className="w-full md:w-auto shrink-0 flex items-center gap-2">
                                        {selectedIdType && (
                                            <>
                                                {/* Case 1: NO File Uploaded -> Show Upload Button */}
                                                {!((selectedIdType === 'PAN' && tempData.pan_file) || (selectedIdType === 'AADHAAR' && tempData.aadhaar_file)) ? (
                                                    <label className={`cursor-pointer px-3 sm:px-4 h-[36px] sm:h-[40px] flex items-center justify-center gap-2 bg-red-50 border-2 ${errors[selectedIdType === 'PAN' ? 'pan_file' : 'aadhaar_file'] ? 'border-red-500 ring-1 ring-red-50' : 'border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} rounded-xl transition-all shadow-sm`}>
                                                        <FiUpload className={`text-[14px] sm:text-[16px] ${errors[selectedIdType === 'PAN' ? 'pan_file' : 'aadhaar_file'] ? 'text-red-500' : 'text-[#C30010]'}`} />
                                                        <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${errors[selectedIdType === 'PAN' ? 'pan_file' : 'aadhaar_file'] ? 'text-red-500' : 'text-[#C30010]'}`}>Upload & Scan *</span>
                                                        <input
                                                            type="file"
                                                            className="hidden"
                                                            accept="image/jpeg,image/png,image/jpg"
                                                            onChange={(e) => handleIdentityUpload(selectedIdType, e.target.files[0])}
                                                        />
                                                    </label>
                                                ) : (
                                                    /* Case 2: File Exists -> Show Filename Pill + View + Delete */
                                                    <>
                                                        {/* Filename Pill (Acts as label) */}
                                                        <div className="flex items-center gap-2 px-3 h-[38px] bg-red-50 border border-red-100 rounded-xl text-red-600 max-w-[200px]" title={selectedIdType === 'PAN' ? tempData.pan_local_filename : tempData.aadhaar_local_filename}>
                                                            <FiFileText className="shrink-0" size={14} />
                                                            <span className="text-xs font-semibold truncate">
                                                                {selectedIdType === 'PAN'
                                                                    ? (tempData.pan_local_filename || 'Uploaded Document')
                                                                    : (tempData.aadhaar_local_filename || 'Uploaded Document')}
                                                            </span>
                                                        </div>

                                                        {/* View Icon */}
                                                        <a
                                                            href={selectedIdType === 'PAN' ? tempData.pan_file : tempData.aadhaar_file}
                                                            target="_blank"
                                                            className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-[#C30010] hover:bg-red-50 transition-all shadow-sm"
                                                            title="View Document"
                                                        >
                                                            <FiEye size={18} />
                                                        </a>

                                                        {/* Delete Icon */}
                                                        <button
                                                            onClick={() => {
                                                                if (selectedIdType === 'PAN') {
                                                                    setTempData(prev => ({ ...prev, pan_file: '', pan_number: '', pan_filename: '' }));
                                                                    setVerificationStatus(prev => ({ ...prev, PAN: 'idle' }));
                                                                }
                                                                if (selectedIdType === 'AADHAAR') {
                                                                    setTempData(prev => ({ ...prev, aadhaar_file: '', aadhaar_number: '', aadhaar_filename: '' }));
                                                                    setVerificationStatus(prev => ({ ...prev, AADHAAR: 'idle' }));
                                                                }
                                                            }}
                                                            className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-500 hover:bg-red-50 transition-all shadow-sm"
                                                            title="Delete"
                                                        >
                                                            <FiTrash2 size={18} />
                                                        </button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {['PAN', 'AADHAAR'].map((type) => {
                                    const fileUrl = type === 'PAN' ? data.pan_file : data.aadhaar_file;
                                    const number = type === 'PAN' ? data.pan_number : data.aadhaar_number;
                                    const isVerified = verificationStatus[type] === 'verified';

                                    if (!number && !fileUrl) return null; // Don't show empty rows in Read Mode

                                    return (
                                        <div key={type} className={`relative p-4 rounded-lg border ${isVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50'}`}>
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg bg-white border border-gray-100 shadow-sm text-gray-600`}>
                                                        {type === 'PAN' ? <FiCreditCard /> : <FaIdCard />}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-800 text-sm">{type}</h4>
                                                        <p className="text-xs font-mono font-semibold text-gray-600 mt-0.5">{number || '---'}</p>
                                                    </div>
                                                </div>
                                                {isVerified && (
                                                    <div className="bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                                                        <FiCheckCircle size={12} />
                                                    </div>
                                                )}
                                            </div>
                                            {fileUrl && (
                                                <div className="mt-3 flex justify-end">
                                                    <a href={fileUrl} target="_blank" className="text-[10px] font-bold text-[#C30010] hover:underline flex items-center gap-1.5 uppercase tracking-wider">
                                                        <FiEye className="text-[#C30010]" size={14} /> View Document
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                                {/* Fallback if nothing added */}
                                {(!data.pan_number && !data.aadhaar_number) && (
                                    <div className="text-gray-400 text-xs italic col-span-full">No verified identity proofs added.</div>
                                )}
                            </div>
                        )}
                    </SectionCard>


                    {/* 4. Bank Details */}
                    < SectionCard title="Bank Details" icon={FiCreditCard} isEditing={editingSection === 'bank'} onEdit={isReadOnly ? null : () => startEdit('bank')} onSave={handleSave} onCancel={cancelEdit} >
                        {editingSection === 'bank' ? (
                            <div className="space-y-4">
                                {(tempData.bank_details || []).map((bank, idx) => (
                                    <div key={idx} className={`p-4 rounded border relative bg-gradient-to-r ${getBankColor(bank.bank_name)} text-white`}>
                                        <button onClick={() => removeRow('bank_details', idx)} className="absolute top-2 right-2 text-white/70 hover:text-white"><FiTrash2 size={14} /></button>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                                    Account Number {errors[`bank_${idx}_acc`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_acc`]})</span>}
                                                </label>
                                                <input
                                                    className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_acc`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400`}
                                                    value={bank.account_number}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, '').slice(0, 18);
                                                        handleDeepChange('bank_details', idx, 'account_number', val);
                                                    }}
                                                    onBlur={() => {
                                                        if (bank.account_number && bank.ifsc && bank.verification_status !== 'Verified') {
                                                            handleVerifyBank(idx);
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {/* IFSC Code - Moved to Position 2 */}
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                                    IFSC Code {errors[`bank_${idx}_ifsc`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_ifsc`]})</span>}
                                                </label>
                                                <input
                                                    className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_ifsc`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400 uppercase font-mono tracking-widest`}
                                                    value={bank.ifsc}
                                                    placeholder="SBIN0001234"
                                                    onChange={(e) => handleDeepChange('bank_details', idx, 'ifsc', e.target.value.toUpperCase())}
                                                    onBlur={async (e) => {
                                                        const code = e.target.value;
                                                        if (code.length === 11) {
                                                            const loadToast = toast.loading("Checking IFSC...");
                                                            try {
                                                                const res = await fetch(`https://ifsc.razorpay.com/${code}`);
                                                                if (res.ok) {
                                                                    const data = await res.json();
                                                                    handleDeepChange('bank_details', idx, 'bank_name', data.BANK);
                                                                    handleDeepChange('bank_details', idx, 'branch', data.BRANCH);
                                                                    handleDeepChange('bank_details', idx, 'bank_address', data.ADDRESS);

                                                                    toast.success("Bank & Branch Found!", { id: loadToast });

                                                                    // Automatic Bank Verification Trigger
                                                                    if (bank.account_number && bank.verification_status !== 'Verified') {
                                                                        handleVerifyBank(idx);
                                                                    }
                                                                } else {
                                                                    toast.error("Invalid IFSC Code", { id: loadToast });
                                                                }
                                                            } catch (err) {
                                                                toast.dismiss(loadToast);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {/* Name as per Passbook - Moved to Position 3 */}
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Name as per Passbook</label>
                                                <input className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400 uppercase" value={bank.holder_name} onChange={(e) => handleDeepChange('bank_details', idx, 'holder_name', e.target.value)} />
                                            </div>

                                            {/* Bank Name - Moved to Position 4 */}
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Bank Name</label>
                                                <input
                                                    className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_name`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900`}
                                                    value={bank.bank_name}
                                                    placeholder="Bank Name"
                                                    onChange={(e) => handleDeepChange('bank_details', idx, 'bank_name', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Branch</label>
                                                <input
                                                    className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400"
                                                    value={bank.branch}
                                                    placeholder="Auto-filled from IFSC"
                                                    onChange={(e) => handleDeepChange('bank_details', idx, 'branch', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                                    Expiry / Valid {errors[`bank_${idx}_expiry`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_expiry`]})</span>}
                                                </label>
                                                <div className="flex gap-2">
                                                    <select
                                                        className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 appearance-none"
                                                        value={(bank.expiry_date || '').split('/')[0] || ''}
                                                        onChange={(e) => {
                                                            const parts = (bank.expiry_date || '').split('/');
                                                            const mm = e.target.value;
                                                            const yyyy = parts[1] || '';
                                                            handleDeepChange('bank_details', idx, 'expiry_date', mm && yyyy ? `${mm}/${yyyy}` : (mm ? `${mm}/` : ''));
                                                        }}
                                                    >
                                                        <option value="">Month</option>
                                                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                                                            <option key={m} value={m}>{new Date(2000, parseInt(m) - 1).toLocaleString('default', { month: 'short' })}</option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 appearance-none"
                                                        value={(bank.expiry_date || '').split('/')[1] || ''}
                                                        onChange={(e) => {
                                                            const parts = (bank.expiry_date || '').split('/');
                                                            const mm = parts[0] || '';
                                                            const yyyy = e.target.value;
                                                            handleDeepChange('bank_details', idx, 'expiry_date', mm && yyyy ? `${mm}/${yyyy}` : (yyyy ? `/${yyyy}` : ''));
                                                        }}
                                                    >
                                                        <option value="">Year</option>
                                                        {Array.from({ length: 20 }, (_, i) => String(2026 + i)).map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-3 col-span-full border-t border-white/10 pt-3 mt-1">
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        {bank.is_active ? (
                                                            <span className="bg-green-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg w-fit uppercase shadow-sm">Active Account</span>
                                                        ) : (
                                                            <span className="bg-black/30 text-white/80 text-[10px] font-bold px-3 py-1.5 rounded-lg w-fit uppercase border border-white/20">Inactive</span>
                                                        )}
                                                        {bank.bank_address && (
                                                            <span className="text-[9px] text-white/70 max-w-full sm:max-w-[200px] truncate bg-black/10 px-2 py-1 rounded" title={bank.bank_address}>
                                                                {bank.bank_address}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {bank.verification_status === 'Verified' ? (
                                                            <div className="flex items-center gap-1.5 bg-green-500/30 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-green-400/50 shadow-sm">
                                                                <FiCheckCircle className="shrink-0" /> <span className="whitespace-nowrap">Identity Verified</span>
                                                            </div>
                                                        ) : bank.verification_status === 'Failed' ? (
                                                            <div className="flex items-center gap-1.5 bg-red-500/30 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-red-400/50 shadow-sm">
                                                                <FiShield className="shrink-0" /> <span className="whitespace-nowrap">Validation Failed</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1.5 bg-white/10 text-white/70 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-white/20 animate-pulse">
                                                                <FiShield className="shrink-0" /> <span className="whitespace-nowrap">Validation Pending</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {bank.verified_name && (
                                                    <div className="text-[10px] text-white/90 font-mono bg-black/20 px-3 py-2 rounded-xl w-full text-left sm:text-right border border-white/5">
                                                        <span className="opacity-70 uppercase tracking-widest mr-2">Registered Name:</span>
                                                        <span className="font-bold">{bank.verified_name}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(tempData.bank_details || []).length < 2 && (
                                    <button onClick={() => addRow('bank_details')} className="px-5 py-2 bg-[#C30010] text-white rounded-xl text-xs font-bold hover:bg-[#a0000d] transition-all flex items-center gap-2 w-fit shadow-lg shadow-red-100 uppercase tracking-widest">
                                        <FiPlus size={16} /> Add New Account
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(data.bank_details || []).map((b, i) => (
                                    <div key={i} className={`p-4 rounded-lg shadow-md relative overflow-hidden bg-gradient-to-r ${getBankColor(b.bank_name)} text-white ${b.is_active ? 'ring-4 ring-offset-2 ring-green-500' : 'opacity-80 grayscale-[0.5]'}`}>
                                        <div className="relative z-10">
                                            <div className="flex justify-between items-start">
                                                <div className="text-xs text-white/80 uppercase tracking-widest">{b.bank_name}</div>
                                                {b.is_active && <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-white/30 backdrop-blur-sm">Active</span>}
                                            </div>
                                            <div className="text-xl font-mono my-2 tracking-wider">{b.account_number}</div>
                                            <div className="flex justify-between text-xs text-white/90 mt-4">
                                                <span>{b.holder_name}</span>
                                                <div className="text-right">
                                                    <div>{b.ifsc}</div>
                                                    {b.expiry_date && <div className="text-[9px] text-white/70 mt-0.5">Valid Upto: {b.expiry_date}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <FiCreditCard className="absolute -bottom-4 -right-4 text-white opacity-20" size={100} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </SectionCard >

                    {/* 5. Community & Other */}
                    < div className="grid grid-cols-1 md:grid-cols-2 gap-6" >
                        <SectionCard title="Community & Languages" icon={FiGlobe} isEditing={editingSection === 'community'} onEdit={isReadOnly ? null : () => startEdit('community')} onSave={handleSave} onCancel={cancelEdit}>
                            {editingSection === 'community' ? (
                                <div className="space-y-4">
                                    <InputField label="Community" name="community" value={tempData.community} onChange={handleChange} options={config.communities} as="select" />
                                    <SearchableSelect label="Caste" value={tempData.caste} onChange={(val) => setTempData({ ...tempData, caste: val })} options={config.castes} placeholder="Select Caste" />
                                    <InputField label="Religion" name="religion" value={tempData.religion} onChange={handleChange} options={config.religions} as="select" />

                                    <div className="mt-4">
                                        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">Languages Known (Speak)</label>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-[12.5px] font-medium focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] transition-all resize-none" rows={3} placeholder="Tamil,English,Hindi..." value={(tempData.languages_speak || []).join(',')} onChange={(e) => setTempData({ ...tempData, languages_speak: e.target.value.split(',') })} />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Field label="Community" value={data.community} />
                                    <Field label="Caste" value={data.caste} />
                                    <Field label="Religion" value={data.religion} />
                                    <div className="mt-2 pt-2 border-t">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase">Languages (Speak)</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {(data.languages_speak || []).map((l, i) => <span key={i} className="px-2 py-0.5 bg-[#ffebee] text-[#C30010] text-xs rounded border border-red-100">{l.trim()}</span>)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </SectionCard>

                        <SectionCard title="Health & Insurance" icon={FiActivity} isEditing={editingSection === 'health'} onEdit={isReadOnly ? null : () => startEdit('health')} onSave={handleSave} onCancel={cancelEdit}>
                            {editingSection === 'health' ? (
                                <div className="space-y-4">
                                    <InputField label="Health Conditions (if any & remedies)" name="health_conditions" value={tempData.health_conditions} onChange={handleChange} as="textarea" />
                                    <div className="p-5 border border-gray-100 rounded-xl bg-gray-50/50 shadow-inner">
                                        <div className="flex justify-between items-center mb-5">
                                            <span className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">Employee Insurance</span>
                                            <div className="flex bg-white rounded-xl overflow-hidden border border-gray-200 p-1 shadow-sm">
                                                <button className={`px-5 py-2 text-[10px] font-bold rounded-lg transition-all ${tempData.insurance_opt === 'OPT-IN' ? 'bg-[#C30010] text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`} onClick={() => setTempData({ ...tempData, insurance_opt: 'OPT-IN' })}>OPT-IN</button>
                                                <button className={`px-5 py-2 text-[10px] font-bold rounded-lg transition-all ${tempData.insurance_opt === 'OPT-OUT' ? 'bg-gray-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`} onClick={() => setTempData({ ...tempData, insurance_opt: 'OPT-OUT' })}>OPT-OUT</button>
                                            </div>
                                        </div>
                                        {tempData.insurance_opt === 'OPT-IN' && (
                                            <div className="space-y-3 animate-fade-in pl-2 border-l-2 border-[#C30010]">
                                                <InputField label="Nominee Name" name="nominee_name" value={tempData.nominee_name} onChange={handleChange} required error={errors.nominee_name} />
                                                <InputField label="Relationship" name="nominee_relationship" value={tempData.nominee_relationship} onChange={handleChange} required error={errors.nominee_relationship} />
                                                <DatePickerField label="Nominee Date of Birth" selected={tempData.nominee_dob} onChange={(d) => handleDateChange('nominee_dob', d)} required error={errors.nominee_dob} />
                                                <PhoneInput label="Nominee Phone" name="nominee_phone" value={tempData.nominee_phone} onChange={handleChange} required error={errors.nominee_phone} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="text-sm">
                                        <span className="text-[10px] uppercase text-gray-400 font-bold block">Health Conditions</span>
                                        {data.health_conditions || 'None'}
                                    </div>
                                    <div className="pt-2 border-t">
                                        <span className="text-[10px] uppercase text-gray-400 font-bold block mb-1">Insurance</span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${data.insurance_opt === 'OPT-IN' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{data.insurance_opt}</span>
                                        {/* Don't show sensitive nominee info in detailed view unless edited, or show minimal? User didn't specify. Showing if present. */}
                                        {data.insurance_opt === 'OPT-IN' && (
                                            <div className="mt-2 space-y-1">
                                                <div className="text-xs text-gray-700 font-bold">
                                                    Nominee: <span className="text-gray-900">{data.nominee_name}</span> ({data.nominee_relationship})
                                                </div>
                                                {data.nominee_phone && (
                                                    <a href={`tel:${data.nominee_phone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-1.5 text-[11px] text-blue-600 font-bold hover:text-blue-700 transition">
                                                        <FiPhone size={11} />
                                                        <span>{data.nominee_phone}</span>
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </SectionCard>

                    </div >
                </div >
            </div >
        </
        div>
    );
}


